// Messenger — Slack-style layout with multi-persona AI support.
import { useEffect, useRef, useState } from "react";
import { LinkifiedText } from "../../shared/linkify";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { useAuth } from "../../shared/stores/auth";
import { I } from "../../shared/Icon";
import { Loading } from "../../shared/components/Empty";
import { toast } from "../../shared/stores/toasts";
import { ChatDashboard } from "./ChatDashboard";
import { RoomIconPicker } from "../../shared/RoomIconPicker";

// ── Emoji Palette data ────────────────────────────────────────────────────────
const EMOJI_CATS = [
  { label: "표정·사람", icon: "😊", emojis: ["😀","😃","😄","😁","😆","😅","🤣","😂","🙂","😊","😇","🥰","😍","🤩","😘","😗","☺️","😚","😙","😋","😛","😜","🤪","😝","🤑","🤗","🤭","🤫","😎","🥸"] },
  { label: "동물·자연", icon: "🐾", emojis: ["🐶","🐱","🐭","🐹","🐰","🦊","🐻","🐼","🐨","🐯","🦁","🐮","🐷","🐸","🐵","🐔","🐧","🐦","🦄","🐝","🦋","🐌","🐢","🐍","🦎","🐳","🦈","🐬","🦭","🌿"] },
  { label: "활동·스포츠", icon: "⚽", emojis: ["⚽","🏀","🏈","⚾","🥎","🎾","🏐","🏉","🥏","🎱","🏓","🏸","🥊","🎯","🎳","🎮","🎲","🎭","🎨","🎸","🎹","🎺","🎻","🥁","🎷","🎤","🎧","🎼","🎬","🎪"] },
  { label: "업무·사물", icon: "💼", emojis: ["💼","📁","📂","📋","📊","📈","📉","📝","✏️","🖊️","📌","📍","🗓️","📅","⏰","💡","🔍","🔑","🗝️","🔒","💰","💳","📞","📱","💻","🖥️","⌨️","🖱️","🔧","⚙️"] },
  { label: "심볼·기타",  icon: "✨", emojis: ["✨","🌟","💫","⭐","🔥","💥","🌈","☀️","🌙","⚡","❄️","🌊","🍀","🌸","🌺","🌻","🌹","💐","🎁","🎀","🎉","🎊","🏆","🥇","🎖️","🚀","🛸","💎","👑","🤖"] },
];

// ── Types ──────────────────────────────────────────────────────────────────────
type RoomType = "DIRECT" | "GROUP" | "DEPARTMENT" | "PROJECT" | "OPEN";
type Room = { id: number; type: RoomType; name: string | null; member_count: number; description?: string | null; icon?: string | null };
type ChatFileMeta = { key: string; name: string; size: number; mime?: string | null };
type Message = {
  id: number; room_id: number; sender_id: number; type: string;
  content: string | null; created_at: string; reply_to_id?: number | null;
  attachments?: { files?: ChatFileMeta[] } | null;
  persona_name?: string; persona_emoji?: string; persona_slug?: string;
};
type Member = { id: number; name: string; username?: string | null; is_admin?: boolean; is_bot?: boolean; avatar_emoji?: string | null };
type UserSummary = { id: number; name: string; username?: string };
type Persona = { id: number; name: string; slug: string; avatar_emoji: string; system_prompt: string; model_id: number | null; user_id: number | null; is_active: boolean };
type RoomPersonaItem = { id: number; room_id: number; persona_id: number; max_loops: number; is_active: boolean; persona: Persona };

type MsgGroup = {
  key: string;
  sender_id: number;
  stype: "me" | "human" | "bot";
  persona_name?: string; persona_emoji?: string; persona_slug?: string;
  messages: Message[];
};

// ── Quick reaction emojis ─────────────────────────────────────────────────────
const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🎉", "🔥", "👀"];

// ── @mention renderer ─────────────────────────────────────────────────────────
// 메시지 글자: 주소는 새 탭 링크로, 나머지는 @멘션 강조로 넘긴다. 주소를 먼저 떼어 내야
// 주소 속 '@'(https://x.com/@id 등)가 멘션으로 잘못 칠해지지 않는다.
// 링크 색은 말풍선 글자색을 따른다 — 내 말풍선은 보라 바탕에 흰 글씨라 파란 링크가 안 보인다.
const CHAT_LINK_STYLE = { color: "inherit", textDecoration: "underline", wordBreak: "break-all" } as const;

function renderMsgContent(text: string, botAvatarMap: Record<string, string>) {
  return (
    <LinkifiedText text={text} linkStyle={CHAT_LINK_STYLE}
      renderText={(chunk) => mentionParts(chunk, botAvatarMap)}/>
  );
}

// Highlights @slug tokens in message text; bot slugs get the bot emoji prefix.
function mentionParts(text: string, botAvatarMap: Record<string, string>) {
  const parts = text.split(/(@[a-zA-Z0-9_]+)/g);
  return parts.map((part, i) => {
    const m = part.match(/^@([a-zA-Z0-9_]+)$/);
    if (!m) return part;
    const slug = m[1].toLowerCase();
    const isBot = slug in botAvatarMap;
    return (
      <span key={i} style={{
        background: isBot ? "rgba(99,102,241,0.18)" : "rgba(99,102,241,0.1)",
        color: "var(--p-400)", borderRadius: 4, padding: "0 3px 1px",
        fontWeight: 700, fontSize: "0.92em", whiteSpace: "nowrap",
      }}>
        {isBot ? `${botAvatarMap[slug]} ` : ""}{part}
      </span>
    );
  });
}

// ── 가벼운 마크다운 렌더러 ──────────────────────────────────────────────────────
// AI 응답이 마크다운(### 제목, **볼드**, * 목록, `코드`, ---)으로 오는데 원문 그대로
// 보여 지저분했다. 라이브러리 없이 React 노드로 직접 조립한다(HTML 주입 없음 = XSS 안전).

// 인라인: **볼드** · `코드` — 나머지는 @멘션 하이라이트로 위임
function inlineMd(text: string, botAvatarMap: Record<string, string>): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*\n]+\*\*|`[^`\n]+`)/g);
  return parts.map((p, i) => {
    if (/^\*\*[^*\n]+\*\*$/.test(p)) {
      return <strong key={i} style={{ fontWeight: 800 }}>{renderMsgContent(p.slice(2, -2), botAvatarMap)}</strong>;
    }
    if (/^`[^`\n]+`$/.test(p)) {
      return <code key={i} style={{ fontFamily: "monospace", fontSize: "0.9em", background: "rgba(127,127,127,0.18)", borderRadius: 4, padding: "1px 5px" }}>{p.slice(1, -1)}</code>;
    }
    return <span key={i}>{renderMsgContent(p, botAvatarMap)}</span>;
  });
}

// 블록: 줄 단위로 제목(#~####)·글머리표(*·-)·번호 목록·구분선(---)·빈 줄 처리
function renderRich(text: string, botAvatarMap: Record<string, string>): React.ReactNode {
  const lines = text.split("\n");
  return (
    <>
      {lines.map((ln, i) => {
        const h = ln.match(/^(#{1,4})\s+(.*)$/);
        if (h) {
          const level = h[1].length;
          return (
            <div key={i} style={{ fontWeight: 800, fontSize: level <= 2 ? "1.18em" : level === 3 ? "1.09em" : "1.02em", margin: i === 0 ? "0 0 3px" : "8px 0 3px" }}>
              {inlineMd(h[2], botAvatarMap)}
            </div>
          );
        }
        if (/^\s*(-{3,}|\*{3,})\s*$/.test(ln)) {
          return <div key={i} style={{ height: 1, background: "currentColor", opacity: 0.22, margin: "8px 0" }} />;
        }
        const qt = ln.match(/^>\s?(.*)$/);
        if (qt) {
          return (
            <div key={i} style={{ borderLeft: "3px solid rgba(127,127,127,0.45)", paddingLeft: 8, opacity: 0.78, fontSize: "0.94em" }}>
              {inlineMd(qt[1], botAvatarMap)}
            </div>
          );
        }
        const b = ln.match(/^(\s*)[*•-]\s+(.*)$/);
        if (b) {
          return (
            <div key={i} style={{ display: "flex", gap: 7, paddingLeft: 4 + Math.min(b[1].length, 8) * 7 }}>
              <span style={{ opacity: 0.75 }}>•</span>
              <span style={{ flex: 1, minWidth: 0 }}>{inlineMd(b[2], botAvatarMap)}</span>
            </div>
          );
        }
        const n = ln.match(/^(\s*)(\d{1,2})[.)]\s+(.*)$/);
        if (n) {
          return (
            <div key={i} style={{ display: "flex", gap: 7, paddingLeft: 4 + Math.min(n[1].length, 8) * 7 }}>
              <span style={{ opacity: 0.85, fontWeight: 700 }}>{n[2]}.</span>
              <span style={{ flex: 1, minWidth: 0 }}>{inlineMd(n[3], botAvatarMap)}</span>
            </div>
          );
        }
        if (!ln.trim()) return <div key={i} style={{ height: 8 }} />;
        return <div key={i}>{inlineMd(ln, botAvatarMap)}</div>;
      })}
    </>
  );
}

// ── 채팅 첨부파일 ──────────────────────────────────────────────────────────────
function fmtSize(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// 다운로드는 인증 헤더가 필요해 <a href> 대신 axios blob 으로 받는다.
async function downloadChatFile(msgId: number, idx: number, name: string) {
  try {
    const r = await api.get(`/chat/files/${msgId}/${idx}`, { responseType: "blob", timeout: 0 });
    const url = URL.createObjectURL(r.data as Blob);
    const a = document.createElement("a");
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch (e: any) {
    toast.error("다운로드 실패", e?.response?.status === 404 ? "파일 원본이 없습니다" : "");
  }
}

// 이미지 미리보기 — <img src> 로는 인증 헤더를 못 실어 blob → objectURL 로 표시.
function ChatImage({ msgId, idx, name }: { msgId: number; idx: number; name: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true; let obj: string | null = null;
    api.get(`/chat/files/${msgId}/${idx}`, { responseType: "blob", timeout: 0 })
      .then((r) => { if (!alive) return; obj = URL.createObjectURL(r.data as Blob); setUrl(obj); })
      .catch(() => {});
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [msgId, idx]);
  if (!url) {
    return <div style={{ width: 200, height: 120, borderRadius: 8, background: "rgba(0,0,0,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "var(--text-3)" }}>🖼 {name}</div>;
  }
  return (
    <img src={url} alt={name} title={`${name} — 클릭해서 크게 보기`}
      onClick={() => window.open(url!, "_blank")}
      style={{ maxWidth: 260, maxHeight: 220, borderRadius: 8, cursor: "zoom-in", display: "block" }} />
  );
}

// 메시지에 붙은 첨부 목록 (이미지 = 미리보기, 그 외 = 다운로드 칩)
function AttachmentRows({ msg, mine }: { msg: Message; mine: boolean }) {
  const files = msg.attachments?.files ?? [];
  if (!files.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: msg.content ? 8 : 0 }}>
      {files.map((f, i) =>
        (f.mime || "").startsWith("image/") ? (
          <ChatImage key={i} msgId={msg.id} idx={i} name={f.name} />
        ) : (
          <button key={i} onClick={() => downloadChatFile(msg.id, i, f.name)}
            title="클릭해서 다운로드"
            style={{
              display: "flex", alignItems: "center", gap: 8, padding: "7px 11px",
              borderRadius: 8, cursor: "pointer", textAlign: "left", maxWidth: 280,
              border: mine ? "1px solid rgba(255,255,255,0.35)" : "1px solid var(--border)",
              background: mine ? "rgba(255,255,255,0.14)" : "var(--bg-0)",
              color: mine ? "#fff" : "var(--text)",
            }}>
            <span style={{ fontSize: 17, flexShrink: 0 }}>📄</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 12.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
              <span style={{ fontSize: 10.5, opacity: 0.75 }}>{fmtSize(f.size)} · ⬇ 다운로드</span>
            </span>
          </button>
        )
      )}
    </div>
  );
}

// ── Constants ──────────────────────────────────────────────────────────────────
const COMMANDS = [
  { cmd: "/help",    icon: "❓", desc: "사용 가능한 명령어 보기" },
  { cmd: "/clear",   icon: "🗑️",  desc: "채팅창 초기화" },
  { cmd: "/members", icon: "👥", desc: "멤버 패널 열기/닫기" },
  { cmd: "/call",    icon: "📹", desc: "화상통화 시작" },
  { cmd: "/tim",     icon: "🤖", desc: "Tim AI에게 직접 질문" },
];

const ROOM_ICON: Record<RoomType, string> = {
  GROUP: "#", DEPARTMENT: "🏢", DIRECT: "💬", PROJECT: "📁", OPEN: "🌐",
};

// ── Utility ────────────────────────────────────────────────────────────────────
function buildGroups(messages: Message[], meId: number): MsgGroup[] {
  const groups: MsgGroup[] = [];
  for (const msg of messages) {
    const isMe = msg.sender_id === meId && msg.type !== "BOT";
    const stype: MsgGroup["stype"] = msg.type === "BOT" ? "bot" : isMe ? "me" : "human";
    const last = groups[groups.length - 1];
    const diffMin = last
      ? (new Date(msg.created_at).getTime() - new Date(last.messages[last.messages.length - 1].created_at).getTime()) / 60000
      : Infinity;
    const same =
      last && last.sender_id === msg.sender_id && last.stype === stype && diffMin < 5 &&
      (last.persona_slug ?? "") === (msg.persona_slug ?? "");
    if (same) {
      last.messages.push(msg);
    } else {
      groups.push({
        key: `${msg.id}-${msg.created_at}`,
        sender_id: msg.sender_id, stype,
        persona_name: msg.persona_name, persona_emoji: msg.persona_emoji, persona_slug: msg.persona_slug,
        messages: [msg],
      });
    }
  }
  return groups;
}

const sameDay = (a: string, b: string) => new Date(a).toDateString() === new Date(b).toDateString();
const fmtDate = (s: string) => {
  const d = new Date(s), today = new Date(), yest = new Date(today);
  yest.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "오늘";
  if (d.toDateString() === yest.toDateString()) return "어제";
  return d.toLocaleDateString("ko-KR", { month: "long", day: "numeric", weekday: "short" });
};
const fmtTime = (s: string) => new Date(s).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });

// ── Main ───────────────────────────────────────────────────────────────────────
export default function ChatPage() {
  const [params, setParams] = useSearchParams();
  const roomIdParam  = Number(params.get("room")) || null;
  const showCreate   = params.get("modal") === "create";
  const showAiManage = params.get("modal") === "ai";

  const [rooms,    setRooms]   = useState<Room[] | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [members,  setMembers]  = useState<Member[]>([]);
  const [input,    setInput]    = useState("");
  const [sending,  setSending]  = useState(false);

  // Derive active room from URL param
  const active = rooms ? (rooms.find((r) => r.id === roomIdParam) ?? null) : null;

  const [typingPersonas, setTypingPersonas] = useState<Map<string, { name: string; emoji: string }>>(new Map());
  const [typingPhase, setTypingPhase] = useState<"thinking" | "typing">("thinking");

  const [showMembers, setShowMembers] = useState(false);
  const [showInfo,    setShowInfo]    = useState(true);
  const [membersKey,  setMembersKey]  = useState(0); // bumped every time members reload
  const [callUrl,     setCallUrl]     = useState<string | null>(null);
  const [replyTo,     setReplyTo]     = useState<Message | null>(null);
  const [reactions,   setReactions]   = useState<Record<number, Record<string, { count: number; mine: boolean }>>>({});
  // 읽음 확인 — user_id → 그 사람이 읽은 마지막 메시지 id (봇은 서버에서 제외됨)
  const [readState,   setReadState]   = useState<Record<number, number>>({});

  const [mentionAnchor, setMentionAnchor] = useState<{ start: number; query: string } | null>(null);
  const [cmdOpen,  setCmdOpen]  = useState(false);
  const [popupIdx, setPopupIdx] = useState(0);
  const [uploading, setUploading] = useState<string | null>(null);   // 업로드 진행 문구 (null=대기)
  const [dragOver,  setDragOver]  = useState(false);   // 파일 드래그 중 오버레이
  const [msgCtx, setMsgCtx] = useState<{ x: number; y: number; msg: Message } | null>(null); // 메시지 우클릭 메뉴
  const [hasMoreOlder, setHasMoreOlder] = useState(false);   // 위로 더 불러올 이전 대화가 있는가
  const [loadingOlder, setLoadingOlder] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);              // 메시지 스크롤 컨테이너
  const skipAutoScroll = useRef(false);                      // 이전 대화 로딩 시 하단 자동 스크롤 금지

  const wsRef     = useRef<WebSocket | null>(null);
  const inputRef  = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const me    = useAuth((s) => s.user);
  const token = useAuth((s) => s.accessToken);
  const [userMap, setUserMap] = useState<Record<number, string>>({});

  // ── Popup lists ────────────────────────────────────────────────────────────
  const filteredMembers: Member[] = mentionAnchor !== null
    ? members.filter((m) => {
        const q = mentionAnchor.query.toLowerCase();
        return !q || m.name.toLowerCase().includes(q) || (m.username?.toLowerCase() ?? "").includes(q);
      }).slice(0, 6)
    : [];
  const filteredCmds = cmdOpen
    ? COMMANDS.filter((c) => c.cmd.startsWith(input.toLowerCase())).slice(0, 5) : [];
  const popupOpen = (mentionAnchor !== null && filteredMembers.length > 0) || (cmdOpen && filteredCmds.length > 0);
  const popupLen  = mentionAnchor !== null ? filteredMembers.length : filteredCmds.length;
  useEffect(() => { setPopupIdx(0); }, [filteredMembers.length, filteredCmds.length]);

  // ── Modal helper ───────────────────────────────────────────────────────────
  const closeModal = () => setParams((p) => { const np = new URLSearchParams(p); np.delete("modal"); return np; }, { replace: true });

  // ── Data load ──────────────────────────────────────────────────────────────
  useEffect(() => {
    api.get<any>("/users?size=300").then((r) => {
      const list: UserSummary[] = Array.isArray(r.data) ? r.data : (r.data?.data ?? r.data?.items ?? []);
      const m: Record<number, string> = {};
      list.forEach((u) => { m[u.id] = u.name; });
      setUserMap(m);
    }).catch(() => {});
  }, []);

  const loadRooms = () => api.get<Room[]>("/chat/rooms").then((r) => setRooms(r.data));
  useEffect(() => { loadRooms(); }, []);

  // 보관된 방은 /chat/rooms 목록에 없다 — URL 로 직접 열면(보관함에서 클릭) 단건 조회로 보충.
  useEffect(() => {
    if (!rooms || !roomIdParam || rooms.some((r) => r.id === roomIdParam)) return;
    api.get<Room>(`/chat/rooms/${roomIdParam}`)
      .then((r) => setRooms((prev) => prev?.some((x) => x.id === r.data.id) ? prev : [...(prev ?? []), r.data]))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, roomIdParam]);

  useEffect(() => {
    if (!active) return;
    api.get<Message[]>(`/chat/rooms/${active.id}/messages`)
      .then((r) => { setMessages(r.data); setHasMoreOlder(r.data.length >= 50); })
      .catch(() => setMessages([]));
    loadMembers();
    loadReads();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  const loadMembers = async () => {
    if (!active) return;
    try { const r = await api.get<Member[]>(`/chat/rooms/${active.id}/members`); setMembers(r.data); setMembersKey((k) => k + 1); }
    catch { setMembers([]); }
  };

  // 방 멤버들의 읽음 커서를 한 번에 가져온다. 이후 갱신은 WS read:update가 담당.
  const loadReads = async () => {
    if (!active) return;
    try {
      const r = await api.get<{ user_id: number; last_read_msg_id: number }[]>(`/chat/rooms/${active.id}/reads`);
      const m: Record<number, number> = {};
      r.data.forEach((x) => { m[x.user_id] = x.last_read_msg_id ?? 0; });
      setReadState(m);
    } catch { setReadState({}); }
  };

  // ── WebSocket ──────────────────────────────────────────────────────────────
  // 끊기면 다시 붙는다. 예전에는 onclose 처리가 없어서, 서버가 재시작하거나 토큰이
  // 만료돼 한 번 끊기면 그 방의 실시간 갱신(새 메시지·읽음 표시)이 조용히 죽었다.
  // 방을 옮기거나 새로고침하기 전까지는 멀쩡해 보여서 더 나빴다.
  useEffect(() => {
    if (!token || !active) return;
    const roomId = active.id;
    let closed = false;
    let retry: number | undefined;
    let attempt = 0;

    const connect = () => {
      if (closed) return;
      const wsProto = location.protocol === "https:" ? "wss" : "ws";
      const base = import.meta.env.VITE_WS_URL ?? `${wsProto}://${location.host}/ws`;
      // 토큰은 붙기 직전에 스토어에서 다시 읽는다. 훅이 잡아 둔 값은 이미 만료됐을 수
      // 있고, 만료된 토큰으로 재연결하면 서버가 4401 로 끊어 재시도만 계속 돈다.
      const tk = useAuth.getState().accessToken || token;
      const ws = new WebSocket(`${base}/chat?token=${encodeURIComponent(tk)}`);
      wsRef.current = ws;
      ws.onopen = () => {
        // 끊겨 있던 동안 오간 메시지와 읽음 상태를 놓쳤다. 다시 맞춘다.
        if (attempt > 0) {
          api.get<Message[]>(`/chat/rooms/${roomId}/messages`)
            .then((r) => setMessages(r.data)).catch(() => {});
          loadReads();
        }
        attempt = 0;
      };
      ws.onmessage = (e) => {
      try {
        const ev = JSON.parse(e.data);
        if (ev.type === "message:new" && ev.payload?.room_id === active.id) {
          const incoming: Message = ev.payload;
          setMessages((prev) => {
            const deduped = (incoming.sender_id === (me?.id ?? 0) && incoming.id > 0)
              ? prev.filter((m) => !(m.id < 0 && m.content === incoming.content)) : prev;
            if (deduped.some((m) => m.id === incoming.id)) return deduped;
            return [...deduped, incoming];
          });
          if (incoming.type === "BOT") {
            const slug = incoming.persona_slug || "tim";
            setTypingPersonas((prev) => { const n = new Map(prev); n.delete(slug); return n; });
          }
          // 방을 보고 있는 동안 남의 메시지가 오면 즉시 읽음 처리 → 발신자에게 '읽음'이 전파된다.
          if (incoming.sender_id !== (me?.id ?? 0) && document.visibilityState === "visible") {
            markRead();
          }
        }
        if (ev.type === "message:updated" && ev.payload?.room_id === active.id) {
          setMessages((prev) => prev.map((m) => m.id === ev.payload.id ? { ...m, content: ev.payload.content } : m));
        }
        if (ev.type === "message:deleted" && ev.payload?.room_id === active.id) {
          setMessages((prev) => prev.filter((m) => m.id !== ev.payload.id));
        }
        if (ev.type === "read:update" && ev.payload?.room_id === active.id) {
          const { user_id, last_read_msg_id } = ev.payload;
          setReadState((prev) => (
            (prev[user_id] ?? 0) >= last_read_msg_id ? prev : { ...prev, [user_id]: last_read_msg_id }
          ));
        }
        if (ev.type === "bot:typing" && ev.payload?.room_id === active.id) {
          const { persona_slug: slug = "tim", persona_name: name = "Tim AI", persona_emoji: emoji = "🤖", typing } = ev.payload;
          setTypingPersonas((prev) => {
            const n = new Map(prev);
            typing === true ? n.set(slug, { name, emoji }) : n.delete(slug);
            return n;
          });
        }
      } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (closed) return;
        attempt += 1;
        // 지수 백오프(1s→30s). 서버가 죽어 있는 동안 4초마다 두드리면 로그만 더럽히고
        // 되살아나는 데 도움도 안 된다.
        const wait = Math.min(30000, 1000 * 2 ** Math.min(attempt, 5));
        retry = window.setTimeout(connect, wait);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };

    connect();
    return () => {
      closed = true;
      if (retry) window.clearTimeout(retry);
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, active?.id]);

  // Reset per-room UI on room switch
  useEffect(() => { setTypingPersonas(new Map()); setShowMembers(false); setReadState({}); }, [roomIdParam]);

  // 탭이 다시 보이면 밀린 메시지를 읽음 처리한다.
  useEffect(() => {
    if (!active) return;
    markRead();   // 방을 연 순간에도 읽음 처리 → 사이드바 배지가 바로 사라진다
    const onVisible = () => {
      if (document.visibilityState === "visible") markRead();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => { document.removeEventListener("visibilitychange", onVisible); window.removeEventListener("focus", onVisible); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  useEffect(() => {
    if (skipAutoScroll.current) { skipAutoScroll.current = false; return; }   // 이전 대화 로딩 중엔 위치 유지
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, typingPersonas.size]);

  useEffect(() => {
    if (typingPersonas.size === 0) return;
    setTypingPhase("thinking");
    const t = setTimeout(() => setTypingPhase("typing"), 3000);
    return () => clearTimeout(t);
  }, [typingPersonas.size]);

  // ── 포커스 유지 ─────────────────────────────────────────────────────────────
  // 입력창에 커서가 계속 머무르도록 한다. preventScroll로 목록이 튀지 않게.
  const focusInput = () => requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));

  // 방을 열거나 바꾸면 바로 타이핑할 수 있게 커서를 입력창에 둔다.
  useEffect(() => { if (active) focusInput(); }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // 입력창 밖에서 글자를 치면 커서를 입력창으로 되돌린다(슬랙/카톡 동작).
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName;
      // 다른 입력 요소나 버튼/링크에 포커스가 있으면 그쪽 동작을 뺏지 않는다.
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || tag === "BUTTON" || tag === "A" || t?.isContentEditable) return;
      // 한 글자 키(글자·숫자·기호)만 가로챈다. Enter/방향키/단축키는 그대로 둔다.
      if (e.key.length !== 1) return;
      const el = inputRef.current;
      if (!el || document.activeElement === el) return;
      el.focus({ preventScroll: true });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  // Textarea auto-resize
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 140) + "px";
  }, [input]);

  // ── Input handlers ─────────────────────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    const cursor = e.target.selectionStart ?? val.length;
    const before = val.slice(0, cursor);
    const atMatch = before.match(/@([\w가-힣]*)$/);
    if (atMatch) { setMentionAnchor({ start: before.lastIndexOf("@"), query: atMatch[1] }); setCmdOpen(false); return; }
    setMentionAnchor(null);
    if (val.startsWith("/") && !val.includes(" ")) setCmdOpen(true); else setCmdOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (popupOpen) {
      if (e.key === "ArrowDown")  { e.preventDefault(); setPopupIdx((i) => (i + 1) % popupLen); return; }
      if (e.key === "ArrowUp")    { e.preventDefault(); setPopupIdx((i) => (i - 1 + popupLen) % popupLen); return; }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        mentionAnchor !== null ? selectMention(filteredMembers[popupIdx]) : execCmd(filteredCmds[popupIdx].cmd);
        return;
      }
      if (e.key === "Escape") { e.preventDefault(); setMentionAnchor(null); setCmdOpen(false); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const handleBlur = () => setTimeout(() => { setMentionAnchor(null); setCmdOpen(false); }, 150);

  const selectMention = (m: Member | undefined) => {
    if (!m || mentionAnchor === null) return;
    const tag = `@${m.username || m.name}`;
    setInput(input.slice(0, mentionAnchor.start) + tag + " " + input.slice(mentionAnchor.start + 1 + mentionAnchor.query.length));
    setMentionAnchor(null);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const execCmd = (cmd: string) => {
    setCmdOpen(false); setInput("");
    if (cmd === "/clear")   { setMessages([]); return; }
    if (cmd === "/members") { setShowMembers((v) => !v); return; }
    if (cmd === "/call")    { startCall(); return; }
    if (cmd === "/tim")     { setInput("@Tim "); requestAnimationFrame(() => inputRef.current?.focus()); return; }
    if (cmd === "/help")    { toast.info("명령어", COMMANDS.map((c) => `${c.cmd} — ${c.desc}`).join("  ·  ")); }
  };

  // ── Send ────────────────────────────────────────────────────────────────────
  const send = async () => {
    if (!input.trim() || !active || sending) return;
    setMentionAnchor(null); setCmdOpen(false);
    const text = input.trim(); setInput(""); setSending(true);
    const replyId = replyTo?.id && replyTo.id > 0 ? replyTo.id : undefined;
    setReplyTo(null);
    const tempId = -Date.now();
    setMessages((prev) => [...prev, { id: tempId, room_id: active.id, sender_id: me?.id ?? 0, type: "TEXT", content: text, created_at: new Date().toISOString(), reply_to_id: replyId }]);
    try { await api.post<Message>(`/chat/rooms/${active.id}/messages`, { type: "TEXT", content: text, reply_to_id: replyId }); }
    catch (e: any) { toast.error("전송 실패", e?.response?.data?.error?.message ?? ""); setMessages((p) => p.filter((m) => m.id !== tempId)); setInput(text); setReplyTo(replyId ? messages.find(m => m.id === replyId) ?? null : null); }
    finally { setSending(false); focusInput(); }
  };

  // ── 파일 업로드 → 첨부 메시지 전송 (📎 버튼·드래그앤드롭 공용) ────────────────
  const uploadFiles = async (list: FileList | File[]) => {
    if (!active || uploading) return;
    const files = Array.from(list).slice(0, 10);   // 한 번에 최대 10개 (파일당 최대 10GB)
    if (!files.length) return;
    setUploading("준비 중…");
    try {
      const metas: ChatFileMeta[] = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post<ChatFileMeta>(`/chat/rooms/${active.id}/files`, fd, {
          timeout: 0,
          onUploadProgress: (ev) => {
            const pct = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0;
            setUploading(`"${f.name}" ${pct}%${files.length > 1 ? ` (${i + 1}/${files.length})` : ""}`);
          },
        });
        metas.push(r.data);
      }
      const allImages = metas.every((m) => (m.mime || "").startsWith("image/"));
      const caption = input.trim();
      const r2 = await api.post<Message>(`/chat/rooms/${active.id}/messages`, {
        type: allImages ? "IMAGE" : "FILE",
        content: caption,
        attachments: { files: metas },
      });
      if (caption) setInput("");
      // WS 로도 오지만 즉시 반영 (id 중복은 WS 핸들러가 걸러 준다)
      setMessages((prev) => prev.some((m) => m.id === r2.data.id) ? prev : [...prev, r2.data]);
    } catch (e: any) {
      toast.error("파일 전송 실패", e?.response?.data?.error?.message ?? e?.message ?? "");
    } finally {
      setUploading(null);
      focusInput();
    }
  };

  // 이전 대화 불러오기 — 새로 참여한 멤버도 방의 과거 대화 전체를 거슬러 볼 수 있다.
  // (서버는 참여 시점과 무관하게 전체 이력을 준다; 한 번에 50개씩 페이지네이션)
  const loadOlder = async () => {
    if (!active || loadingOlder) return;
    const firstId = messages.find((m) => m.id > 0)?.id;
    if (!firstId) return;
    setLoadingOlder(true);
    try {
      const r = await api.get<Message[]>(`/chat/rooms/${active.id}/messages`, { params: { before_id: firstId, limit: 50 } });
      const el = listRef.current;
      const prevH = el?.scrollHeight ?? 0;
      skipAutoScroll.current = true;
      setMessages((prev) => {
        const ids = new Set(prev.map((m) => m.id));
        return [...r.data.filter((m) => !ids.has(m.id)), ...prev];
      });
      setHasMoreOlder(r.data.length >= 50);
      // 붙인 만큼 스크롤을 내려 보던 위치를 유지한다
      requestAnimationFrame(() => { if (el) el.scrollTop += el.scrollHeight - prevH; });
    } catch (e: any) {
      toast.error("이전 대화 불러오기 실패", e?.response?.data?.error?.message ?? "");
    } finally { setLoadingOlder(false); }
  };

  // 읽음 처리 + 사이드바 안읽음 배지 갱신
  const markRead = () => {
    if (!active) return;
    api.post(`/chat/rooms/${active.id}/read`)
      .then(() => window.dispatchEvent(new Event("chat-rooms-updated")))
      .catch(() => {});
  };

  // ── 메시지 우클릭 메뉴 액션 ─────────────────────────────────────────────────
  const roomBot = members.find((m) => m.is_bot && m.username);   // 방의 첫 AI (멘션 대상)

  // 메뉴에서 만든 문장을 바로 전송 (AI 멘션 명령 등)
  const sendQuick = async (text: string) => {
    if (!active) return;
    try {
      const r = await api.post<Message>(`/chat/rooms/${active.id}/messages`, { type: "TEXT", content: text });
      setMessages((prev) => prev.some((m) => m.id === r.data.id) ? prev : [...prev, r.data]);
    } catch (e: any) {
      toast.error("전송 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  // 메시지를 지칭하는 짧은 라벨 — 내용 앞부분 또는 첨부 이름
  const msgSnippet = (m: Message) => {
    const t = (m.content ?? "").trim();
    if (t) return `"${t.slice(0, 40)}${t.length > 40 ? "…" : ""}"`;
    const f = m.attachments?.files?.[0];
    return f ? `"${f.name}" 파일` : "해당 메시지";
  };

  const summarizeTopic = (m: Message) => {
    if (!roomBot) { toast.info("방에 AI가 없습니다", "「멤버 관리」에서 AI를 추가하세요"); return; }
    sendQuick(`@${roomBot.username} ${msgSnippet(m)} 메시지부터 지금까지 이어진 대화 내용을 핵심만 정리해서 요약해줘`);
  };

  const summarizeFiles = (m: Message) => {
    if (!roomBot) { toast.info("방에 AI가 없습니다", "「멤버 관리」에서 AI를 추가하세요"); return; }
    const names = (m.attachments?.files ?? []).map((f) => `"${f.name}"`).join(", ");
    sendQuick(`@${roomBot.username} 첨부파일 ${names} 의 내용을 요약해줘`);
  };

  const copyMsg = (m: Message) => {
    const text = (m.content ?? "").trim() || (m.attachments?.files ?? []).map((f) => f.name).join(", ");
    navigator.clipboard?.writeText(text).then(() => toast.success("복사됨")).catch(() => toast.error("복사 실패"));
  };

  // 인용하여 답장 — 원문을 > 인용 블록으로 입력창에 넣는다
  const quoteReply = (m: Message) => {
    const q = (m.content ?? "").split("\n").slice(0, 6).map((l) => `> ${l}`).join("\n");
    setInput((prev) => `${q}\n${prev}`);
    focusInput();
  };

  const translateMsg = (m: Message) => {
    if (!roomBot) { toast.info("방에 AI가 없습니다", "「멤버 관리」에서 AI를 추가하세요"); return; }
    sendQuick(`@${roomBot.username} 다음 메시지를 자연스럽게 번역해줘 (한국어면 영어로, 외국어면 한국어로):\n"${(m.content ?? "").slice(0, 500)}"`);
  };

  const draftReply = (m: Message) => {
    if (!roomBot) { toast.info("방에 AI가 없습니다", "「멤버 관리」에서 AI를 추가하세요"); return; }
    sendQuick(`@${roomBot.username} ${msgSnippet(m)} 메시지에 대한 답장 초안을 정중하고 간결하게 제안해줘`);
  };

  // 메시지 수정(본인) / 삭제(본인·방 관리자) — 서버가 방 전체에 실시간 반영한다
  const editMsg = async (m: Message) => {
    const next = window.prompt("메시지 수정", m.content ?? "");
    if (next == null || !next.trim() || next.trim() === m.content) return;
    try {
      const r = await api.patch<Message>(`/chat/messages/${m.id}`, { content: next.trim() });
      setMessages((prev) => prev.map((x) => x.id === m.id ? { ...x, content: r.data.content } : x));
    } catch (e: any) { toast.error("수정 실패", e?.response?.data?.error?.message ?? ""); }
  };

  const deleteMsg = async (m: Message) => {
    if (!window.confirm("이 메시지를 삭제할까요?")) return;
    try {
      await api.delete(`/chat/messages/${m.id}`);
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
    } catch (e: any) { toast.error("삭제 실패", e?.response?.data?.error?.message ?? ""); }
  };

  const startCall = async () => {
    if (!active) return;
    try { const r = await api.post<{ room_url: string }>("/chat/calls", { chat_room_id: active.id }); setCallUrl(r.data.room_url); }
    catch (e: any) { toast.error("통화 시작 실패", e?.response?.data?.error?.message ?? ""); }
  };

  const addMember = async (uid: number) => {
    if (!active) return;
    try { await api.post(`/chat/rooms/${active.id}/members`, { user_ids: [uid] }); toast.success("멤버 추가됨"); await loadMembers(); loadRooms(); }
    catch (e: any) { toast.error("추가 실패", e?.response?.data?.error?.message ?? ""); }
  };
  const removeMember = async (uid: number) => {
    if (!active) return;
    try { await api.delete(`/chat/rooms/${active.id}/members/${uid}`); toast.success("제거됨"); await loadMembers(); loadRooms(); }
    catch (e: any) { toast.error("실패", e?.response?.data?.error?.message ?? ""); }
  };
  // AI(페르소나) 제거 — 멤버 목록의 봇은 user_id만 있어, 방 페르소나 목록에서 rp_id를 찾아 삭제.
  const removeBot = async (botUserId: number) => {
    if (!active) return;
    try {
      const r = await api.get<RoomPersonaItem[]>(`/chat/rooms/${active.id}/personas`);
      const rp = (r.data || []).find((x) => x.persona?.user_id === botUserId);
      if (!rp) { toast.error("해당 AI를 찾을 수 없습니다"); return; }
      await api.delete(`/chat/rooms/${active.id}/personas/${rp.id}`);
      toast.success("AI 제거됨"); await loadMembers(); loadRooms();
    } catch (e: any) { toast.error("AI 제거 실패", e?.response?.data?.error?.message ?? ""); }
  };

  const nameOf = (senderId: number, type: string, personaName?: string) =>
    type === "BOT" ? (personaName || "Tim AI") : senderId === me?.id ? (me?.name || "나") : (userMap[senderId] || `#${senderId}`);

  const addReaction = (msgId: number, emoji: string) => {
    if (msgId < 0) return; // skip temp messages
    focusInput();
    setReactions((prev) => {
      const r = { ...(prev[msgId] ?? {}) };
      if (r[emoji]?.mine) {
        const c = r[emoji].count - 1;
        if (c <= 0) { const copy = { ...r }; delete copy[emoji]; return { ...prev, [msgId]: copy }; }
        return { ...prev, [msgId]: { ...r, [emoji]: { count: c, mine: false } } };
      }
      return { ...prev, [msgId]: { ...r, [emoji]: { count: (r[emoji]?.count || 0) + 1, mine: true } } };
    });
  };

  const handleReply = (msg: Message) => {
    setReplyTo(msg);
    focusInput();
  };

  const groups = buildGroups(messages, me?.id ?? -1);
  // 읽음 확인 — 나를 뺀 사람 멤버. (봇/AI 페르소나는 서버의 /reads가 이미 제외한다)
  const readerIds = Object.keys(readState).map(Number).filter((uid) => uid !== (me?.id ?? -1));
  // '읽음' 문구는 최신 내 메시지 하나에만 붙여 목록이 지저분해지지 않게 한다.
  const lastOwnMsgId = messages.reduce(
    (acc, m) => (m.sender_id === (me?.id ?? -1) && m.type !== "BOT" && m.id > acc ? m.id : acc), 0,
  );
  // Map persona slug → current avatar emoji (refreshes when members reload)
  const botAvatarMap = Object.fromEntries(
    members.filter((m) => m.is_bot && m.username).map((m) => [m.username!, m.avatar_emoji ?? "🤖"])
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>

      {/* ═══ MAIN AREA ════════════════════════════════════════════════════════ */}
      <div
        onDragOver={(e) => { if (active && Array.from(e.dataTransfer.types ?? []).includes("Files")) { e.preventDefault(); setDragOver(true); } }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false); }}
        onDrop={(e) => { if (!active) return; e.preventDefault(); setDragOver(false); if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files); }}
        style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0, position: "relative" }}>

        {/* 파일 드래그 중 오버레이 */}
        {active && dragOver && (
          <div style={{
            position: "absolute", inset: 8, zIndex: 60, pointerEvents: "none",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(99,102,241,0.12)", border: "3px dashed var(--p-500,#6366f1)", borderRadius: 14,
          }}>
            <div style={{ background: "var(--bg-0,#fff)", color: "var(--text-1)", fontSize: 14.5, fontWeight: 700, padding: "13px 22px", borderRadius: 12, boxShadow: "0 6px 26px rgba(0,0,0,0.22)" }}>
              📎 여기에 놓으면 파일이 전송됩니다
            </div>
          </div>
        )}

        {!active ? (
          /* No room selected → chat dashboard (채팅 현황 · AI 리스트 · 멤버 리스트) */
          <ChatDashboard />
        ) : (
          <>
            {/* Room header */}
            <div style={{ padding: "11px 18px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: 17, color: "var(--text-3)" }}>{active.icon || ROOM_ICON[active.type] || "#"}</span>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{active.name || `방 ${active.id}`}</h3>
                </div>
                {active.description && <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{active.description}</div>}
              </div>
              <div style={{ display: "flex", gap: 4, flexShrink: 0, alignItems: "center" }}>
                <button className="t-btn" style={{ fontSize: 12 }} onClick={() => setShowMembers((v) => !v)}>멤버 관리</button>
                <button className="t-btn" style={{ fontSize: 17 }} onClick={startCall} title="화상통화">📹</button>
                <button
                  className="t-btn"
                  style={{ fontSize: 12, background: showInfo ? "var(--p-500)" : undefined, color: showInfo ? "#fff" : undefined }}
                  onClick={() => setShowInfo((v) => !v)}
                  title="참여자 정보"
                >
                  👥
                </button>
              </div>
            </div>

            {/* Call bar */}
            {callUrl && (
              <div style={{ background: "rgba(99,102,241,0.07)", borderBottom: "1px solid var(--border)", padding: "7px 18px", display: "flex", gap: 10, alignItems: "center", fontSize: 13, flexShrink: 0 }}>
                <span>📹</span>
                <a href={callUrl} target="_blank" rel="noreferrer" style={{ color: "var(--p-500)", fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{callUrl}</a>
                <button className="t-btn solid" style={{ fontSize: 12 }} onClick={() => window.open(callUrl, "_blank")}>참여</button>
                <button className="t-btn" onClick={() => setCallUrl(null)}>✕</button>
              </div>
            )}

            {/* Messages + panel */}
            <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

              {/* Message list */}
              <div ref={listRef} style={{ flex: 1, overflowY: "auto", paddingBottom: 4, minWidth: 0 }}>

                {/* 이전 대화 불러오기 — 참여 전 이력 포함 전체 열람 */}
                {hasMoreOlder && (
                  <div style={{ textAlign: "center", padding: "10px 0 2px" }}>
                    <button className="t-btn" onClick={loadOlder} disabled={loadingOlder} style={{ fontSize: 12 }}>
                      {loadingOlder ? "불러오는 중…" : "⬆ 이전 대화 불러오기"}
                    </button>
                  </div>
                )}

                {/* Welcome banner */}
                {messages.length === 0 && (
                  <div style={{ padding: "36px 20px 12px", textAlign: "left" }}>
                    <div style={{ fontSize: 44, marginBottom: 10 }}>{active.icon || ROOM_ICON[active.type] || "#"}</div>
                    <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>{active.name || `방 ${active.id}`}에 오신 것을 환영합니다</div>
                    <div style={{ fontSize: 13, color: "var(--text-3)" }}>이 채널의 첫 번째 메시지입니다. <strong>@Tim</strong>이나 다른 AI 페르소나를 @멘션하세요.</div>
                    <div style={{ height: 1, background: "var(--border)", margin: "18px 0" }} />
                  </div>
                )}

                {/* Message groups */}
                {groups.map((g, gi) => {
                  const prev = gi > 0 ? groups[gi - 1] : null;
                  const showDiv = prev && !sameDay(prev.messages[0].created_at, g.messages[0].created_at);
                  return (
                    <div key={g.key}>
                      {showDiv && (
                        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 20px 2px", userSelect: "none" }}>
                          <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
                          <span style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 600, whiteSpace: "nowrap", padding: "0 6px" }}>{fmtDate(g.messages[0].created_at)}</span>
                          <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
                        </div>
                      )}
                      <MsgGroupRow group={g} meId={me?.id ?? -1} userMap={userMap} nameOf={nameOf}
                        reactions={reactions} onReact={addReaction} onReply={handleReply} allMessages={messages}
                        botAvatarMap={botAvatarMap}
                        onMsgMenu={(e, msg) => { if (msg.id > 0) { e.preventDefault(); setMsgCtx({ x: e.clientX, y: e.clientY, msg }); } }}
                        readerIds={readerIds} readState={readState} lastOwnMsgId={lastOwnMsgId} />
                    </div>
                  );
                })}

                {/* AI typing indicators (bubble style) */}
                {Array.from(typingPersonas.entries()).map(([slug, info]) => (
                  <div key={slug} style={{ display: "flex", gap: 10, padding: "3px 60px 3px 16px", alignItems: "flex-end" }}>
                    <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(99,102,241,0.18)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0, marginBottom: 18 }}>{info.emoji}</div>
                    <div style={{ display: "flex", flexDirection: "column", maxWidth: "68%" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--p-400)", marginBottom: 4, paddingLeft: 2 }}>
                        {info.name} <span style={{ fontSize: 9, background: "rgba(99,102,241,0.12)", color: "var(--p-500)", padding: "1px 5px", borderRadius: 4, fontWeight: 700 }}>AI</span>
                      </div>
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 14px", borderRadius: "4px 18px 18px 18px", background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.18)" }}>
                        <TypingDots />
                        <span style={{ fontSize: 12, color: "var(--p-500)", fontStyle: "italic" }}>{typingPhase === "thinking" ? "생각 중…" : "입력 중…"}</span>
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              {/* Member management panel */}
              {showMembers && <MemberPanel members={members} meId={me?.id ?? 0} onClose={() => setShowMembers(false)} onAdd={addMember} onRemove={removeMember} onRemoveBot={removeBot} />}
              {/* Right info panel */}
              {showInfo && <RightInfoPanel members={members} roomId={active?.id ?? null} membersKey={membersKey} onClose={() => setShowInfo(false)} />}
            </div>

            {/* ── Input area ──────────────────────────────────────────────── */}
            <div style={{ padding: "0 16px 14px", flexShrink: 0 }}>
              <div style={{ position: "relative", border: "1px solid var(--border)", borderRadius: 10, background: "var(--panel)", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>

                {/* @Mention popup */}
                {mentionAnchor !== null && filteredMembers.length > 0 && (
                  <div style={{ position: "absolute", bottom: "calc(100% + 6px)", left: 0, right: 0, background: "var(--bg-0)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.14)", zIndex: 200, overflow: "hidden" }}>
                    <div style={{ padding: "6px 12px", fontSize: 11, color: "var(--text-3)", fontWeight: 700, borderBottom: "1px solid var(--border)", letterSpacing: "0.05em" }}>멤버 멘션</div>
                    {filteredMembers.map((m, i) => (
                      <button key={m.id} onMouseDown={(e) => { e.preventDefault(); selectMention(m); }} onMouseEnter={() => setPopupIdx(i)}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 12px", width: "100%", border: "none", cursor: "pointer", background: i === popupIdx ? "rgba(99,102,241,0.08)" : "transparent", transition: "background 0.1s" }}>
                        <div style={{ width: 28, height: 28, borderRadius: m.is_bot ? 7 : "50%", background: m.is_bot ? "rgba(99,102,241,0.15)" : "var(--panel)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: m.is_bot ? 15 : 12, fontWeight: 700, flexShrink: 0 }}>
                          {m.is_bot ? (m.avatar_emoji || "🤖") : m.name[0]?.toUpperCase()}
                        </div>
                        <span style={{ fontWeight: 600, fontSize: 13, flex: 1, textAlign: "left", color: "var(--text)" }}>{m.name}</span>
                        {m.username && <span style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "monospace" }}>@{m.username}</span>}
                        {m.is_bot && <span style={{ fontSize: 10, color: "var(--p-500)", fontWeight: 700, background: "rgba(99,102,241,0.1)", padding: "1px 6px", borderRadius: 8 }}>AI</span>}
                      </button>
                    ))}
                  </div>
                )}

                {/* /Command popup */}
                {cmdOpen && filteredCmds.length > 0 && (
                  <div style={{ position: "absolute", bottom: "calc(100% + 6px)", left: 0, right: 0, background: "var(--bg-0)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "0 8px 28px rgba(0,0,0,0.14)", zIndex: 200, overflow: "hidden" }}>
                    <div style={{ padding: "6px 12px", fontSize: 11, color: "var(--text-3)", fontWeight: 700, borderBottom: "1px solid var(--border)" }}>명령어 <span style={{ fontWeight: 400 }}>↑↓ 선택, Enter 실행</span></div>
                    {filteredCmds.map((c, i) => (
                      <button key={c.cmd} onMouseDown={(e) => { e.preventDefault(); execCmd(c.cmd); }} onMouseEnter={() => setPopupIdx(i)}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", width: "100%", border: "none", cursor: "pointer", background: i === popupIdx ? "rgba(99,102,241,0.08)" : "transparent", transition: "background 0.1s" }}>
                        <span style={{ fontSize: 17, width: 24, textAlign: "center", flexShrink: 0 }}>{c.icon}</span>
                        <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 13, color: "var(--p-500)", minWidth: 88 }}>{c.cmd}</span>
                        <span style={{ fontSize: 12, color: "var(--text-3)" }}>{c.desc}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Reply context banner */}
                {replyTo && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", background: "rgba(99,102,241,0.07)", borderBottom: "1px solid rgba(99,102,241,0.12)", borderRadius: "10px 10px 0 0" }}>
                    <span style={{ fontSize: 13 }}>↩️</span>
                    <span style={{ fontSize: 12, flex: 1, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <strong style={{ color: "var(--p-500)" }}>{nameOf(replyTo.sender_id, replyTo.type, replyTo.persona_name)}</strong>
                      {" · "}{replyTo.content?.slice(0, 60)}{(replyTo.content?.length ?? 0) > 60 ? "…" : ""}
                    </span>
                    <button style={{ border: "none", background: "none", cursor: "pointer", fontSize: 14, color: "var(--text-3)", lineHeight: 1, padding: "0 2px" }} onClick={() => setReplyTo(null)}>✕</button>
                  </div>
                )}
                {/* Textarea */}
                <textarea
                  ref={inputRef} value={input} onChange={handleInputChange} onKeyDown={handleKeyDown} onBlur={handleBlur}
                  rows={1}
                  placeholder={`${active.icon || ROOM_ICON[active.type] || "#"} ${active.name || `방 ${active.id}`}에 메시지 보내기…`}
                  style={{ width: "100%", border: "none", outline: "none", background: "transparent", resize: "none", overflow: "hidden", minHeight: 44, maxHeight: 140, padding: "12px 84px 12px 14px", fontSize: 14, fontFamily: "inherit", lineHeight: 1.55, boxSizing: "border-box", color: "var(--text)", display: "block" }}
                />

                {/* 파일 첨부 (📎) — 입력창의 텍스트는 캡션으로 함께 전송된다 */}
                <input ref={fileInputRef} type="file" multiple hidden
                  onChange={(e) => { if (e.target.files?.length) uploadFiles(e.target.files); e.target.value = ""; }} />
                <button onClick={() => fileInputRef.current?.click()} onMouseDown={(e) => e.preventDefault()}
                  disabled={!!uploading} title="파일 첨부 — 파일당 최대 10GB (드래그해서 놓아도 됩니다)"
                  style={{ position: "absolute", right: 44, bottom: 8, width: 32, height: 32, borderRadius: 8, border: "none", cursor: uploading ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", fontSize: 17, color: "var(--text-3)" }}>
                  {uploading ? "⏳" : "📎"}
                </button>

                {/* Send button */}
                <button onClick={send} onMouseDown={(e) => e.preventDefault()} disabled={sending || !input.trim()}
                  style={{ position: "absolute", right: 8, bottom: 8, width: 32, height: 32, borderRadius: 8, border: "none", cursor: input.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", background: input.trim() ? "var(--p-500)" : "transparent", transition: "background 0.15s" }}>
                  {sending ? <span style={{ fontSize: 14 }}>…</span> : <I.Send size={14} style={{ color: input.trim() ? "#fff" : "var(--text-3)" }} />}
                </button>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4, paddingLeft: 2 }}>
                {uploading
                  ? <span style={{ color: "var(--p-500)", fontWeight: 700 }}>📎 업로드 중: {uploading}</span>
                  : <><strong>Shift+Enter</strong> 줄바꿈 &nbsp;·&nbsp; <strong>@</strong> 멘션 &nbsp;·&nbsp; <strong>/</strong> 명령어 &nbsp;·&nbsp; <strong>📎</strong> 파일 최대 10GB (드래그 가능)</>}
              </div>
            </div>
          </>
        )}
      </div>

      {/* 메시지 우클릭 메뉴 — 답장·복사·AI 요약·파일 다운로드 */}
      {msgCtx && (() => {
        const m = msgCtx.msg;
        const files = m.attachments?.files ?? [];
        const close = () => setMsgCtx(null);
        const item = (label: string, action: () => void, danger = false) => (
          <button key={label} onClick={() => { close(); action(); }}
            style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13, color: danger ? "#ef4444" : "var(--text)", textAlign: "left", whiteSpace: "nowrap" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(99,102,241,0.08)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "none"; }}>
            {label}
          </button>
        );
        return (
          <>
            <div style={{ position: "fixed", inset: 0, zIndex: 998 }} onClick={close} onContextMenu={(e) => { e.preventDefault(); close(); }} />
            <div style={{
              position: "fixed", left: Math.min(msgCtx.x, window.innerWidth - 250), top: Math.min(msgCtx.y, window.innerHeight - 270), zIndex: 999,
              background: "var(--bg-0)", border: "1px solid var(--border)", borderRadius: 10,
              boxShadow: "0 8px 28px rgba(0,0,0,0.2)", overflow: "hidden", minWidth: 200, padding: "4px 0",
            }}>
              {/* 감정표현 — 누르면 그 메시지에 리액션이 달린다 (다시 누르면 취소) */}
              <div style={{ display: "flex", gap: 2, padding: "6px 10px 8px", borderBottom: "1px solid var(--border)" }}>
                {QUICK_REACTIONS.map((emoji) => (
                  <button key={emoji} onClick={() => { close(); addReaction(m.id, emoji); }}
                    title={`${emoji} 반응 남기기`}
                    style={{ border: "none", background: "none", cursor: "pointer", fontSize: 17, lineHeight: 1, padding: "3px 4px", borderRadius: 6, transition: "transform 0.1s, background 0.1s" }}
                    onMouseEnter={(e) => { const el = e.currentTarget as HTMLButtonElement; el.style.background = "rgba(99,102,241,0.1)"; el.style.transform = "scale(1.25)"; }}
                    onMouseLeave={(e) => { const el = e.currentTarget as HTMLButtonElement; el.style.background = "none"; el.style.transform = "scale(1)"; }}>
                    {emoji}
                  </button>
                ))}
              </div>
              {item("↩️ 답장", () => handleReply(m))}
              {!!m.content && item("📝 인용하여 답장", () => quoteReply(m))}
              {(m.content || files.length > 0) && item("📋 내용 복사", () => copyMsg(m))}
              {files.length > 0 && item("⬇ 파일 다운로드", () => files.forEach((f, i) => downloadChatFile(m.id, i, f.name)))}
              <div style={{ height: 1, background: "var(--border)", margin: "4px 0" }} />
              {item("🤖 이 주제 대화 요약하기", () => summarizeTopic(m))}
              {files.length > 0 && item("🤖 이 파일 요약하기", () => summarizeFiles(m))}
              {!!m.content && item("🤖 번역하기", () => translateMsg(m))}
              {!!m.content && m.sender_id !== me?.id && item("🤖 답장 초안 제안", () => draftReply(m))}
              {m.sender_id === me?.id && m.type !== "BOT" && (
                <>
                  <div style={{ height: 1, background: "var(--border)", margin: "4px 0" }} />
                  {!!m.content && item("✏️ 메시지 수정", () => editMsg(m))}
                  {item("🗑 메시지 삭제", () => deleteMsg(m), true)}
                </>
              )}
            </div>
          </>
        );
      })()}

      {/* Modals */}
      {showCreate && <CreateRoomModal onClose={closeModal} onCreated={(r) => {
        loadRooms();
        window.dispatchEvent(new Event("chat-rooms-updated"));
        setParams({ room: String(r.id) });
      }} />}
      {showAiManage && (
        <AiManageModal roomId={active?.id ?? roomIdParam} roomName={active?.name ?? null}
          onClose={() => { closeModal(); if (active) loadMembers(); }} />
      )}
    </div>
  );
}

// ── Typing dots ───────────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <span style={{ display: "inline-flex", gap: 3, alignItems: "center" }}>
      {[0, 160, 320].map((d) => (
        <span key={d} style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--p-500)", display: "inline-block", animation: `tBounce 1.2s ${d}ms infinite ease-in-out` }} />
      ))}
      <style>{`@keyframes tBounce{0%,60%,100%{transform:translateY(0);opacity:.4}30%{transform:translateY(-5px);opacity:1}}`}</style>
    </span>
  );
}

// ── Message Group Row (bubble layout) ────────────────────────────────────────
function MsgGroupRow({ group, userMap, nameOf, reactions, onReact, onReply, onMsgMenu, allMessages, botAvatarMap, readerIds, readState, lastOwnMsgId }: {
  group: MsgGroup; meId: number; userMap: Record<number, string>;
  nameOf: (id: number, type: string, name?: string) => string;
  reactions: Record<number, Record<string, { count: number; mine: boolean }>>;
  onReact: (msgId: number, emoji: string) => void;
  onReply: (msg: Message) => void;
  onMsgMenu: (e: React.MouseEvent, msg: Message) => void;
  allMessages: Message[];
  botAvatarMap: Record<string, string>;
  readerIds: number[];
  readState: Record<number, number>;
  lastOwnMsgId: number;
}) {
  const [reactPickerPos, setReactPickerPos] = useState<{ top: number; left: number; msgId: number } | null>(null);
  const isMe  = group.stype === "me";
  const isBot = group.stype === "bot";
  const total = group.messages.length;

  const senderName = nameOf(group.sender_id, isBot ? "BOT" : "TEXT", group.persona_name);
  const avatarLabel = isBot
    ? (botAvatarMap[group.persona_slug || ""] || group.persona_emoji || "🤖")
    : (userMap[group.sender_id] || "?")[0]?.toUpperCase();

  // Border-radius helper — tail on top side toward the avatar
  const radius = (idx: number) => {
    if (isMe) {
      if (total === 1)          return "18px 4px 18px 18px";
      if (idx === 0)            return "18px 4px 6px 18px";
      if (idx === total - 1)    return "6px 6px 18px 18px";
      return "6px 6px 6px 18px";
    }
    if (total === 1)            return "4px 18px 18px 18px";
    if (idx === 0)              return "4px 18px 6px 6px";
    if (idx === total - 1)      return "6px 6px 18px 18px";
    return "6px 18px 6px 6px";
  };

  const openReactPicker = (e: React.MouseEvent<HTMLButtonElement>, msgId: number) => {
    const r = e.currentTarget.getBoundingClientRect();
    setReactPickerPos({ top: r.bottom + 6, left: Math.max(8, r.left - 80), msgId });
  };

  // Inline action toolbar — shown on hover via CSS .t-msg-toolbar
  const toolbarFor = (msg: Message, marginTop: number) => (
    <div className="t-msg-toolbar" style={{ marginTop }}>
      <button onClick={(e) => openReactPicker(e, msg.id)} title="반응"
        style={{ width: 27, height: 27, borderRadius: 7, border: "1px solid var(--border)", background: "var(--bg-0)", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
        😊
      </button>
      <button onClick={() => onReply(msg)} title="답글"
        style={{ width: 27, height: 27, borderRadius: 7, border: "1px solid var(--border)", background: "var(--bg-0)", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>
        ↩
      </button>
      <button onClick={() => onReact(msg.id, "👍")} title="좋아요"
        style={{ width: 27, height: 27, borderRadius: 7, border: "1px solid var(--border)", background: "var(--bg-0)", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
        👍
      </button>
    </div>
  );

  // Reply-to quote shown above first bubble
  const replyQuoteFor = (msg: Message) => {
    if (!msg.reply_to_id) return null;
    const orig = allMessages.find((m) => m.id === msg.reply_to_id);
    if (!orig) return null;
    return (
      <div style={{ fontSize: 11, color: "var(--text-3)", padding: "3px 10px", borderRadius: 8, background: "rgba(99,102,241,0.07)", borderLeft: "3px solid var(--p-400)", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        <span style={{ color: "var(--p-400)", fontWeight: 700 }}>{nameOf(orig.sender_id, orig.type, orig.persona_name)}</span>
        {" "}{orig.content?.slice(0, 80)}{(orig.content?.length ?? 0) > 80 ? "…" : ""}
      </div>
    );
  };

  // Reaction badges below each bubble
  const reactionRowFor = (msgId: number) => {
    const r = reactions[msgId];
    if (!r || Object.keys(r).length === 0) return null;
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 3, marginBottom: 1 }}>
        {Object.entries(r).map(([emoji, { count, mine }]) => (
          <button key={emoji} onClick={() => onReact(msgId, emoji)}
            style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "1px 7px", borderRadius: 12, border: `1px solid ${mine ? "var(--p-500)" : "var(--border)"}`, background: mine ? "rgba(99,102,241,0.12)" : "var(--panel)", cursor: "pointer", fontSize: 12, lineHeight: 1.5, color: mine ? "var(--p-500)" : "var(--text)", fontWeight: mine ? 700 : 400 }}>
            <span>{emoji}</span><span style={{ fontSize: 11 }}>{count}</span>
          </button>
        ))}
      </div>
    );
  };

  // 읽음 표시 — 내 메시지 옆. 안 읽은 사람이 있으면 그 수를, 다 읽었으면 '읽음'.
  const readBadge = (msg: Message) => {
    if (!isMe || msg.id <= 0 || readerIds.length === 0) return null;
    const unread = readerIds.filter((uid) => (readState[uid] ?? 0) < msg.id);
    if (unread.length === 0) {
      if (msg.id !== lastOwnMsgId) return null;
      return (
        <span title="모두 읽음" style={{ fontSize: 10, color: "var(--text-3)", whiteSpace: "nowrap", marginBottom: 4, flexShrink: 0 }}>
          읽음
        </span>
      );
    }
    const who = unread.map((uid) => userMap[uid] || `#${uid}`).join(", ");
    return (
      <span title={`안 읽음 — ${who}`} style={{ fontSize: 11, fontWeight: 800, color: "#f59e0b", whiteSpace: "nowrap", marginBottom: 4, flexShrink: 0 }}>
        {unread.length}
      </span>
    );
  };

  const lastMsg = group.messages[group.messages.length - 1];

  // ── MY messages — right aligned ──────────────────────────────────────────
  if (isMe) {
    return (
      <>
        <div className="t-msg-row"
          style={{ display: "flex", flexDirection: "row-reverse", alignItems: "flex-start", gap: 6, padding: "3px 16px 3px 16px" }}
        >
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", maxWidth: "68%" }}>
            {group.messages.map((msg, idx) => (
              <div key={msg.id} style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                {idx === 0 && replyQuoteFor(msg)}
                <div style={{ display: "flex", flexDirection: "row-reverse", alignItems: "flex-end", gap: 5, maxWidth: "100%" }}>
                  <div onContextMenu={(e) => onMsgMenu(e, msg)} style={{
                    background: "var(--p-500, #6366f1)", color: "#fff",
                    padding: "9px 14px", borderRadius: radius(idx), marginBottom: 2,
                    fontSize: 14, lineHeight: 1.65, wordBreak: "break-word" as const,
                    whiteSpace: "pre-wrap" as const, maxWidth: "100%", minWidth: 0,
                    opacity: msg.id < 0 ? 0.6 : 1,
                    boxShadow: "0 1px 4px rgba(99,102,241,0.25)",
                  }}>
                    {msg.content ? renderRich(msg.content, botAvatarMap) : null}
                    <AttachmentRows msg={msg} mine={true} />
                    {msg.id < 0 && <span style={{ fontSize: 10, opacity: 0.75, marginLeft: 8, fontStyle: "italic" }}>전송 중…</span>}
                  </div>
                  {readBadge(msg)}
                </div>
                {reactionRowFor(msg.id)}
              </div>
            ))}
            <span style={{ fontSize: 10, color: "var(--text-3)", marginTop: 2, paddingRight: 2 }}>{fmtTime(group.messages[0].created_at)}</span>
          </div>
          {/* Toolbar appears to the LEFT of my bubbles (row-reverse) */}
          {toolbarFor(lastMsg, 4)}
        </div>
        {reactPickerPos && (
          <QuickReactPicker pos={reactPickerPos}
            onSelect={(emoji) => { onReact(reactPickerPos.msgId, emoji); setReactPickerPos(null); }}
            onClose={() => setReactPickerPos(null)} />
        )}
      </>
    );
  }

  // ── Others (human / bot) — left aligned ──────────────────────────────────
  return (
    <>
      <div className="t-msg-row"
        style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "3px 16px 3px 16px" }}
      >
        {/* Avatar pinned to bottom of bubble stack */}
        <div style={{
          width: 34, height: 34, borderRadius: isBot ? 8 : "50%", flexShrink: 0,
          background: isBot ? "rgba(99,102,241,0.18)" : "var(--panel)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: isBot ? 20 : 14, fontWeight: 700, color: "var(--text)",
          alignSelf: "flex-end", marginBottom: 18,
        }}>
          {avatarLabel}
        </div>

        <div style={{ display: "flex", flexDirection: "column", maxWidth: "68%", flex: 1, minWidth: 0 }}>
          {/* Name + time */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 5, marginBottom: 4, paddingLeft: 2 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: isBot ? "var(--p-400)" : "var(--text)" }}>{senderName}</span>
            {isBot && <span style={{ fontSize: 9, background: "rgba(99,102,241,0.12)", color: "var(--p-500)", padding: "1px 5px", borderRadius: 4, fontWeight: 700 }}>AI</span>}
            <span style={{ fontSize: 10, color: "var(--text-3)" }}>{fmtTime(group.messages[0].created_at)}</span>
          </div>
          {group.messages.map((msg, idx) => (
            <div key={msg.id}>
              {idx === 0 && replyQuoteFor(msg)}
              <div onContextMenu={(e) => onMsgMenu(e, msg)} style={{
                background: isBot ? "rgba(99,102,241,0.08)" : "var(--panel, #f3f4f6)",
                border: isBot ? "1px solid rgba(99,102,241,0.18)" : "1px solid var(--border)",
                color: "var(--text)", padding: "9px 14px", borderRadius: radius(idx), marginBottom: 2,
                fontSize: 14, lineHeight: 1.65, wordBreak: "break-word" as const,
                whiteSpace: "pre-wrap" as const,
              }}>
                {msg.content ? renderRich(msg.content, botAvatarMap) : null}
                <AttachmentRows msg={msg} mine={false} />
              </div>
              {reactionRowFor(msg.id)}
            </div>
          ))}
          <span style={{ fontSize: 10, color: "transparent", marginTop: 2 }}>·</span>
        </div>

        {/* Toolbar appears to the RIGHT of others' bubbles */}
        {toolbarFor(lastMsg, 24)}
      </div>
      {reactPickerPos && (
        <QuickReactPicker pos={reactPickerPos}
          onSelect={(emoji) => { onReact(reactPickerPos.msgId, emoji); setReactPickerPos(null); }}
          onClose={() => setReactPickerPos(null)} />
      )}
    </>
  );
}

// ── Quick React Picker (portal) ───────────────────────────────────────────────
function QuickReactPicker({ pos, onSelect, onClose }: {
  pos: { top: number; left: number };
  onSelect: (emoji: string) => void;
  onClose: () => void;
}) {
  return createPortal(
    <>
      <div style={{ position: "fixed", inset: 0, zIndex: 9990 }} onClick={onClose} />
      <div style={{
        position: "fixed", top: pos.top, left: Math.min(pos.left, window.innerWidth - 300),
        zIndex: 9991, background: "var(--bg-0)", border: "1px solid var(--border)",
        borderRadius: 24, boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
        padding: "6px 8px", display: "flex", gap: 2,
      }}>
        {QUICK_REACTIONS.map((emoji) => (
          <button key={emoji} onClick={() => onSelect(emoji)}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "scale(1.28)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)"; }}
            style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 22, borderRadius: 8, padding: "2px 4px", transition: "transform 0.1s", lineHeight: 1 }}>
            {emoji}
          </button>
        ))}
      </div>
    </>,
    document.body,
  );
}

// ── Member Panel ──────────────────────────────────────────────────────────────
function MemberPanel({ members, meId, onClose, onAdd, onRemove, onRemoveBot }: {
  members: Member[]; meId: number;
  onClose: () => void; onAdd: (uid: number) => void; onRemove: (uid: number) => void; onRemoveBot: (uid: number) => void;
}) {
  const [addSearch, setAddSearch] = useState("");
  const [allUsers, setAllUsers]   = useState<UserSummary[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // Load full user list on mount
  useEffect(() => {
    api.get<any>("/users?size=300")
      .then((r) => setAllUsers(Array.isArray(r.data) ? r.data : (r.data?.data ?? r.data?.items ?? [])))
      .catch(() => {})
      .finally(() => setLoadingUsers(false));
  }, []);

  const memberIds = new Set(members.map((m) => m.id));
  const q = addSearch.trim().toLowerCase();
  // Filter out already-in-room members, filter by search query
  const inviteList = allUsers.filter((u) =>
    !memberIds.has(u.id) && (!q || u.name.toLowerCase().includes(q) || (u.username?.toLowerCase() ?? "").includes(q))
  );

  const bots   = members.filter((m) => m.is_bot);
  const humans = members.filter((m) => !m.is_bot);

  return (
    <div style={{ width: 256, borderLeft: "1px solid var(--border)", background: "var(--bg-0)", display: "flex", flexDirection: "column", overflowY: "auto", flexShrink: 0 }}>
      <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center" }}>
        <span style={{ fontWeight: 800, fontSize: 14, flex: 1 }}>멤버</span>
        <button className="t-btn" style={{ fontSize: 12 }} onClick={onClose}>✕</button>
      </div>

      <div style={{ padding: "12px 16px", flex: 1, overflowY: "auto" }}>
        {/* AI bots */}
        {bots.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>AI 봇 — {bots.length}</div>
            {bots.map((m) => (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0" }}>
                <div style={{ width: 30, height: 30, borderRadius: 7, background: "rgba(99,102,241,0.18)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}>{m.avatar_emoji || "🤖"}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{m.name}</div>
                  {m.username && <div style={{ fontSize: 10, color: "var(--p-500)" }}>@{m.username}</div>}
                </div>
                <span style={{ fontSize: 10, background: "rgba(99,102,241,0.1)", color: "var(--p-500)", padding: "1px 5px", borderRadius: 5, fontWeight: 600 }}>AI</span>
                <button className="t-btn" style={{ fontSize: 10, padding: "2px 6px" }} onClick={() => onRemoveBot(m.id)}>제거</button>
              </div>
            ))}
          </div>
        )}

        {/* Humans */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>멤버 — {humans.length}</div>
          {humans.map((m) => (
            <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0" }}>
              <div style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--panel)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{m.name[0]?.toUpperCase()}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name} {m.is_admin && <span style={{ fontSize: 10, color: "var(--text-3)" }}>·관리자</span>}</div>
                {m.username && <div style={{ fontSize: 10, color: "var(--text-3)" }}>@{m.username}</div>}
              </div>
              {m.id !== meId && <button className="t-btn" style={{ fontSize: 10, padding: "2px 6px" }} onClick={() => onRemove(m.id)}>제거</button>}
            </div>
          ))}
        </div>

        {/* Invite section — full team list + search filter */}
        <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>+ 팀원 초대</div>
          <input className="t-input" style={{ width: "100%", fontSize: 12, marginBottom: 8 }}
            placeholder="이름으로 필터…" value={addSearch}
            onChange={(e) => setAddSearch(e.target.value)} />
          {loadingUsers ? (
            <div style={{ fontSize: 12, color: "var(--text-3)", textAlign: "center", padding: "10px 0" }}>불러오는 중…</div>
          ) : inviteList.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--text-3)", textAlign: "center", padding: "10px 0" }}>
              {q ? "검색 결과 없음" : "초대할 팀원이 없습니다"}
            </div>
          ) : (
            <div style={{ maxHeight: 220, overflowY: "auto" }}>
              {inviteList.map((u) => (
                <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 2px", fontSize: 13 }}>
                  <div style={{ width: 26, height: 26, borderRadius: "50%", background: "var(--panel)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{u.name[0]?.toUpperCase()}</div>
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name}</span>
                  {u.username && <span style={{ fontSize: 10, color: "var(--text-3)" }}>@{u.username}</span>}
                  <button className="t-btn" style={{ fontSize: 11 }} onClick={() => onAdd(u.id)}>추가</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Right Info Panel ─────────────────────────────────────────────────────────
function RightInfoPanel({ members, roomId, membersKey, onClose }: {
  members: Member[]; roomId: number | null; membersKey: number; onClose: () => void;
}) {
  const [roomPersonas, setRoomPersonas] = useState<RoomPersonaItem[]>([]);

  useEffect(() => {
    if (!roomId) return;
    api.get<RoomPersonaItem[]>(`/chat/rooms/${roomId}/personas`)
      .then((r) => setRoomPersonas(r.data)).catch(() => {});
  }, [roomId, membersKey]);

  const bots   = members.filter((m) => m.is_bot);
  const humans = members.filter((m) => !m.is_bot);

  const AvatarCircle = ({ m }: { m: Member }) => (
    <div title={m.name} style={{
      width: 38, height: 38, borderRadius: m.is_bot ? 9 : "50%",
      background: m.is_bot ? "rgba(99,102,241,0.18)" : "var(--panel)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: m.is_bot ? 20 : 13, fontWeight: 700,
      border: "2px solid var(--bg-0)", cursor: "default",
      flexShrink: 0, position: "relative" as const,
    }}>
      {m.is_bot ? (m.avatar_emoji || "🤖") : m.name[0]?.toUpperCase()}
    </div>
  );

  return (
    <div style={{ width: 210, borderLeft: "1px solid var(--border)", background: "var(--bg-1)", display: "flex", flexDirection: "column", flexShrink: 0, overflowY: "auto" }}>
      {/* Header */}
      <div style={{ padding: "12px 14px 10px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        <span style={{ fontWeight: 800, fontSize: 13, flex: 1 }}>참여자</span>
        <button className="t-btn" style={{ fontSize: 11, padding: "2px 6px" }} onClick={onClose}>✕</button>
      </div>

      <div style={{ padding: "12px 14px", flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 18 }}>

        {/* AI 봇 */}
        {bots.length > 0 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", letterSpacing: "0.06em", textTransform: "uppercase" as const, marginBottom: 8 }}>AI 봇 — {bots.length}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
              {bots.map((m) => (
                <div key={m.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                  <AvatarCircle m={m} />
                  <span style={{ fontSize: 9, color: "var(--p-500)", fontWeight: 700, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 42 }}>{m.name}</span>
                </div>
              ))}
            </div>
            {/* AI-to-AI hint when multiple bots are present */}
            {bots.length >= 2 && (
              <div style={{ marginTop: 10, padding: "8px 10px", background: "rgba(99,102,241,0.07)", border: "1px solid rgba(99,102,241,0.18)", borderRadius: 10, fontSize: 10, color: "var(--p-500)", lineHeight: 1.5 }}>
                <div style={{ fontWeight: 800, marginBottom: 3 }}>🔗 AI 연쇄 대화 활성</div>
                <div style={{ color: "var(--text-3)" }}>
                  AI 답변에 <strong style={{ color: "var(--p-400)" }}>@슬러그</strong>를 포함하면 해당 AI가 자동 응답합니다.
                  {bots.length > 0 && (
                    <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 3 }}>
                      {bots.map((b) => b.username && (
                        <span key={b.id} style={{ background: "rgba(99,102,241,0.15)", borderRadius: 5, padding: "1px 5px", fontFamily: "monospace", fontWeight: 700 }}>@{b.username}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* AI 루프 정보 */}
        {roomPersonas.filter((rp) => rp.is_active).length > 0 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", letterSpacing: "0.06em", textTransform: "uppercase" as const, marginBottom: 8 }}>AI 설정</div>
            {roomPersonas.filter((rp) => rp.is_active).map((rp) => (
              <div key={rp.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0", fontSize: 11 }}>
                <span style={{ fontSize: 16 }}>{rp.persona.avatar_emoji}</span>
                <span style={{ flex: 1, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{rp.persona.name}</span>
                <span style={{ fontSize: 10, color: "var(--text-3)", background: "var(--panel)", padding: "1px 5px", borderRadius: 5 }}>×{rp.max_loops}</span>
              </div>
            ))}
          </div>
        )}

        {/* 인원 그리드 */}
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-3)", letterSpacing: "0.06em", textTransform: "uppercase" as const, marginBottom: 8 }}>멤버 — {humans.length}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
            {humans.map((m) => (
              <div key={m.id} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                <AvatarCircle m={m} />
                <span style={{ fontSize: 9, color: "var(--text-2)", textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 42 }}>{m.name}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

// ── AI Manage Modal ───────────────────────────────────────────────────────────
function AiManageModal({ roomId, roomName, onClose }: { roomId: number | null; roomName: string | null; onClose: () => void }) {
  const [tab, setTab] = useState<"room" | "all" | "create">(roomId ? "room" : "all");
  const [allPersonas,  setAllPersonas]  = useState<Persona[]>([]);
  const [roomPersonas, setRoomPersonas] = useState<RoomPersonaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingLoops, setEditingLoops] = useState<Record<number, string>>({});

  // Create form
  const [createForm, setCreateForm] = useState({ name: "", slug: "", avatar_emoji: "🤖", system_prompt: "" });
  const [creating, setCreating] = useState(false);
  const [editPersona, setEditPersona] = useState<Persona | null>(null);
  const [editForm, setEditForm] = useState({ name: "", avatar_emoji: "🤖", system_prompt: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pa] = await Promise.all([api.get<Persona[]>("/chat/personas")]);
      setAllPersonas(pa.data);
      if (roomId) {
        const pr = await api.get<RoomPersonaItem[]>(`/chat/rooms/${roomId}/personas`);
        setRoomPersonas(pr.data);
      }
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const inRoom = new Set(roomPersonas.map((rp) => rp.persona_id));
  const available = allPersonas.filter((p) => !inRoom.has(p.id) && p.is_active);

  const addToRoom = async (personaId: number) => {
    if (!roomId) return;
    try { await api.post(`/chat/rooms/${roomId}/personas`, { persona_id: personaId, max_loops: 3 }); await load(); toast.success("AI 추가됨"); }
    catch (e: any) { toast.error("추가 실패", e?.response?.data?.error?.message ?? ""); }
  };

  const removeFromRoom = async (rpId: number) => {
    if (!roomId) return;
    try { await api.delete(`/chat/rooms/${roomId}/personas/${rpId}`); await load(); toast.success("제거됨"); }
    catch (e: any) { toast.error("실패", e?.response?.data?.error?.message ?? ""); }
  };

  const toggleActive = async (rp: RoomPersonaItem) => {
    if (!roomId) return;
    try { await api.patch(`/chat/rooms/${roomId}/personas/${rp.id}`, { is_active: !rp.is_active }); await load(); }
    catch { /* ignore */ }
  };

  const saveLoops = async (rp: RoomPersonaItem) => {
    const v = parseInt(editingLoops[rp.id] ?? "");
    if (!roomId || isNaN(v) || v < 1 || v > 20) return;
    try {
      await api.patch(`/chat/rooms/${roomId}/personas/${rp.id}`, { max_loops: v });
      setEditingLoops((p) => { const n = { ...p }; delete n[rp.id]; return n; });
      await load();
    } catch { /* ignore */ }
  };

  const toggleGlobalActive = async (p: Persona) => {
    try { await api.patch(`/chat/personas/${p.id}`, { is_active: !p.is_active }); await load(); }
    catch { /* ignore */ }
  };

  const deletePersona = async (id: number) => {
    if (!confirm("이 페르소나를 삭제하시겠습니까?")) return;
    try { await api.delete(`/chat/personas/${id}`); await load(); toast.success("삭제됨"); }
    catch (e: any) { toast.error("삭제 실패", e?.response?.data?.error?.message ?? ""); }
  };

  const createPersona = async () => {
    if (!createForm.name.trim() || !createForm.slug.trim()) return;
    setCreating(true);
    try {
      await api.post("/chat/personas", { name: createForm.name.trim(), slug: createForm.slug.trim(), avatar_emoji: createForm.avatar_emoji || "🤖", system_prompt: createForm.system_prompt });
      toast.success("페르소나 생성됨");
      setCreateForm({ name: "", slug: "", avatar_emoji: "🤖", system_prompt: "" });
      setTab("all");
      await load();
    } catch (e: any) { toast.error("생성 실패", e?.response?.data?.error?.message ?? ""); }
    finally { setCreating(false); }
  };

  const startEdit = (p: Persona) => {
    setEditPersona(p);
    setEditForm({ name: p.name, avatar_emoji: p.avatar_emoji, system_prompt: p.system_prompt });
    setTab("all");
  };

  const saveEdit = async () => {
    if (!editPersona || !editForm.name.trim()) return;
    setSavingEdit(true);
    try {
      await api.patch(`/chat/personas/${editPersona.id}`, { name: editForm.name.trim(), avatar_emoji: editForm.avatar_emoji, system_prompt: editForm.system_prompt });
      toast.success("수정됨");
      setEditPersona(null);
      await load();
    } catch (e: any) { toast.error("수정 실패", e?.response?.data?.error?.message ?? ""); }
    finally { setSavingEdit(false); }
  };

  const TABS: { key: "room" | "all" | "create"; label: string; show?: boolean }[] = [
    { key: "room", label: `이 방의 AI`, show: !!roomId },
    { key: "all",  label: "전체 페르소나" },
    { key: "create", label: "+ 새 AI 만들기" },
  ];

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={(e) => e.stopPropagation()}
        style={{ width: 680, maxHeight: "92vh", display: "flex", flexDirection: "column", padding: 0, overflow: "hidden" }}>

        {/* Header */}
        <div style={{ padding: "18px 20px 0", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 22 }}>🤖</span>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>AI 봇 관리</h3>
              {roomName && <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 1 }}>채널: # {roomName}</div>}
            </div>
            <button className="t-btn" onClick={onClose} style={{ fontSize: 16, lineHeight: 1 }}>✕</button>
          </div>
          {/* Tabs */}
          <div style={{ display: "flex", gap: 0 }}>
            {TABS.filter((t) => t.show !== false).map((t) => (
              <button key={t.key} onClick={() => setTab(t.key as typeof tab)}
                style={{ padding: "8px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: tab === t.key ? 700 : 400, color: tab === t.key ? "var(--p-500)" : "var(--text-3)", borderBottom: tab === t.key ? "2px solid var(--p-500)" : "2px solid transparent", transition: "all 0.15s" }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 20px 28px" }}>
          {loading ? <Loading /> : (
            <>
              {/* TAB: 이 방의 AI */}
              {tab === "room" && roomId && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {/* AI-to-AI info banner when 2+ AIs are active */}
                  {roomPersonas.filter((rp) => rp.is_active).length >= 2 && (
                    <div style={{ padding: "12px 14px", background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.22)", borderRadius: 12 }}>
                      <div style={{ fontWeight: 800, fontSize: 13, color: "var(--p-500)", marginBottom: 4 }}>🔗 AI 간 연쇄 대화 활성화됨</div>
                      <div style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.6 }}>
                        AI 답변 중에 다른 AI의 <code style={{ background: "rgba(99,102,241,0.12)", padding: "0 4px", borderRadius: 4, fontWeight: 700 }}>@슬러그</code>를 언급하면 해당 AI가 자동으로 이어서 응답합니다.
                        <br />
                        <span style={{ color: "var(--text-3)" }}>최대 루프 수로 무한 반복을 방지할 수 있습니다.</span>
                      </div>
                      <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {roomPersonas.filter((rp) => rp.is_active).map((rp) => (
                          <span key={rp.id} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(99,102,241,0.12)", color: "var(--p-500)", padding: "3px 8px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                            <span>{rp.persona.avatar_emoji}</span>
                            <span style={{ fontFamily: "monospace" }}>@{rp.persona.slug}</span>
                            <span style={{ fontSize: 10, color: "var(--text-3)", fontWeight: 400 }}>× 최대 {rp.max_loops}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {roomPersonas.length === 0 && (
                    <div style={{ textAlign: "center", padding: "32px 0", color: "var(--text-3)" }}>
                      <div style={{ fontSize: 36, marginBottom: 8 }}>🤖</div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>이 채널에 AI가 없습니다</div>
                      <div style={{ fontSize: 12, marginTop: 4 }}>아래에서 AI 페르소나를 추가하세요</div>
                      {available.length > 0 && (
                        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 6 }}>
                          {available.slice(0, 3).map((p) => (
                            <button key={p.id} className="t-btn t-btn-primary" style={{ fontSize: 13 }} onClick={() => addToRoom(p.id)}>
                              {p.avatar_emoji} {p.name} 추가
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {roomPersonas.map((rp) => {
                    const loops = editingLoops[rp.id] ?? String(rp.max_loops);
                    return (
                      <div key={rp.id} style={{ border: "1px solid var(--border)", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, background: rp.is_active ? "rgba(99,102,241,0.03)" : "var(--bg-1)", opacity: rp.is_active ? 1 : 0.65 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 10, background: "rgba(99,102,241,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>{rp.persona.avatar_emoji}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontWeight: 800, fontSize: 14 }}>{rp.persona.name}</span>
                            <span style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "monospace" }}>@{rp.persona.slug}</span>
                          </div>
                          <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {rp.persona.system_prompt ? rp.persona.system_prompt.slice(0, 70) + (rp.persona.system_prompt.length > 70 ? "…" : "") : "기본 시스템 프롬프트"}
                          </div>
                        </div>
                        {/* Max loops */}
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, flexShrink: 0 }}>
                          <span style={{ fontSize: 10, color: "var(--text-3)", whiteSpace: "nowrap" }}>최대 루프</span>
                          <input value={loops}
                            onChange={(e) => setEditingLoops((p) => ({ ...p, [rp.id]: e.target.value }))}
                            onBlur={() => saveLoops(rp)}
                            onKeyDown={(e) => e.key === "Enter" && saveLoops(rp)}
                            title="AI 연쇄 응답 최대 횟수 (1~20)"
                            style={{ width: 42, textAlign: "center", fontSize: 13, padding: "3px 4px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--panel)", color: "var(--text)", fontWeight: 700 }} />
                        </div>
                        <button onClick={() => toggleActive(rp)}
                          style={{ fontSize: 11, padding: "4px 10px", border: "1px solid var(--border)", borderRadius: 7, cursor: "pointer", background: rp.is_active ? "rgba(99,102,241,0.15)" : "transparent", color: rp.is_active ? "var(--p-500)" : "var(--text-3)", fontWeight: 600, flexShrink: 0 }}>
                          {rp.is_active ? "활성" : "비활성"}
                        </button>
                        <button onClick={() => removeFromRoom(rp.id)}
                          style={{ fontSize: 11, padding: "4px 10px", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 7, cursor: "pointer", background: "transparent", color: "#ef4444", fontWeight: 600, flexShrink: 0 }}>
                          제거
                        </button>
                      </div>
                    );
                  })}

                  {/* Available to add */}
                  {available.length > 0 && (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-3)", letterSpacing: "0.05em", textTransform: "uppercase", marginTop: 8 }}>추가 가능한 AI</div>
                      {available.map((p) => (
                        <div key={p.id} style={{ border: "1px dashed var(--border)", borderRadius: 12, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                          <div style={{ width: 40, height: 40, borderRadius: 9, background: "var(--panel)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>{p.avatar_emoji}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 13 }}>{p.name} <span style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "monospace" }}>@{p.slug}</span></div>
                            <div style={{ fontSize: 11, color: "var(--text-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.system_prompt.slice(0, 55) || "기본 프롬프트"}</div>
                          </div>
                          <button className="t-btn t-btn-primary" style={{ fontSize: 12, flexShrink: 0 }} onClick={() => addToRoom(p.id)}>+ 추가</button>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* TAB: 전체 페르소나 */}
              {tab === "all" && (
                editPersona ? (
                  /* ── Edit form ── */
                  <div style={{ display: "grid", gap: 18 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <button className="t-btn" style={{ fontSize: 12 }} onClick={() => setEditPersona(null)}>← 목록으로</button>
                      <span style={{ fontWeight: 700, fontSize: 14 }}>{editPersona.name} 수정</span>
                    </div>
                    <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                      <div>
                        <label className="t-label">이모지</label>
                        <EmojiPicker value={editForm.avatar_emoji} onChange={(e) => setEditForm({ ...editForm, avatar_emoji: e })} />
                      </div>
                      <div style={{ flex: 1, display: "grid", gap: 12 }}>
                        <div>
                          <label className="t-label">AI 이름 *</label>
                          <input className="t-input" value={editForm.name} placeholder="AI 이름"
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "monospace", paddingTop: 2 }}>
                          @{editPersona.slug} <span style={{ fontWeight: 400 }}>(슬러그는 변경 불가)</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                        <label className="t-label" style={{ margin: 0 }}>시스템 프롬프트</label>
                        <span style={{ fontSize: 11, color: "var(--text-3)" }}>{editForm.system_prompt.length}자</span>
                      </div>
                      <textarea className="t-input" rows={14}
                        style={{ resize: "vertical", fontFamily: "inherit", lineHeight: 1.65, fontSize: 13, minHeight: 260 }}
                        value={editForm.system_prompt}
                        onChange={(e) => setEditForm({ ...editForm, system_prompt: e.target.value })} />
                    </div>
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                      <button className="t-btn" onClick={() => setEditPersona(null)}>취소</button>
                      <button className="t-btn t-btn-primary" onClick={saveEdit} disabled={savingEdit || !editForm.name.trim()}>
                        {savingEdit ? "저장 중…" : "저장"}
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── Persona list ── */
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {allPersonas.length === 0 && (
                      <div style={{ textAlign: "center", padding: "32px 0", color: "var(--text-3)" }}>
                        <div style={{ fontSize: 36, marginBottom: 8 }}>✨</div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>아직 AI 페르소나가 없습니다</div>
                        <button className="t-btn t-btn-primary" style={{ marginTop: 12, fontSize: 13 }} onClick={() => setTab("create")}>+ 첫 번째 AI 만들기</button>
                      </div>
                    )}
                    {allPersonas.map((p) => (
                      <div key={p.id} style={{ border: "1px solid var(--border)", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, opacity: p.is_active ? 1 : 0.55 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 10, background: "rgba(99,102,241,0.14)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, flexShrink: 0 }}>{p.avatar_emoji}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 800, fontSize: 14 }}>{p.name}</span>
                            <span style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "monospace" }}>@{p.slug}</span>
                            {!p.is_active && <span style={{ fontSize: 10, background: "var(--panel)", color: "var(--text-3)", padding: "1px 5px", borderRadius: 4 }}>비활성</span>}
                            {inRoom.has(p.id) && <span style={{ fontSize: 10, background: "rgba(99,102,241,0.12)", color: "var(--p-500)", padding: "1px 5px", borderRadius: 4, fontWeight: 600 }}>이 방에 있음</span>}
                          </div>
                          <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.system_prompt.slice(0, 70) || "기본 시스템 프롬프트"}</div>
                        </div>
                        <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                          {roomId && !inRoom.has(p.id) && p.is_active && (
                            <button className="t-btn" style={{ fontSize: 11 }} onClick={() => addToRoom(p.id)}>+ 방 추가</button>
                          )}
                          <button onClick={() => startEdit(p)}
                            style={{ fontSize: 11, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 7, cursor: "pointer", background: "transparent", color: "var(--text)", fontWeight: 600 }}>
                            수정
                          </button>
                          <button onClick={() => toggleGlobalActive(p)}
                            style={{ fontSize: 11, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 7, cursor: "pointer", background: "transparent", color: "var(--text-3)" }}>
                            {p.is_active ? "비활성화" : "활성화"}
                          </button>
                          <button onClick={() => deletePersona(p.id)}
                            style={{ fontSize: 11, padding: "4px 8px", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 7, cursor: "pointer", background: "transparent", color: "#ef4444" }}>
                            삭제
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}

              {/* TAB: 새 AI 만들기 */}
              {tab === "create" && (
                <div style={{ display: "grid", gap: 18 }}>
                  {/* Row 1: 이모지 팔레트 + 이름 + 슬러그 */}
                  <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                    {/* Emoji picker */}
                    <div>
                      <label className="t-label">이모지</label>
                      <EmojiPicker value={createForm.avatar_emoji} onChange={(e) => setCreateForm({ ...createForm, avatar_emoji: e })} />
                    </div>
                    {/* Name + slug */}
                    <div style={{ flex: 1, display: "grid", gap: 12 }}>
                      <div>
                        <label className="t-label">AI 이름 *</label>
                        <input className="t-input" placeholder="예: 마케팅 AI, 코드 리뷰어, 법무 자문" value={createForm.name}
                          onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
                      </div>
                      <div>
                        <label className="t-label">슬러그 * <span style={{ fontWeight: 400, fontSize: 10, color: "var(--text-3)" }}>— @멘션 호출명 (영문소문자·숫자·밑줄)</span></label>
                        <input className="t-input" placeholder="예: marketing_ai"
                          value={createForm.slug}
                          onChange={(e) => setCreateForm({ ...createForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })} />
                      </div>
                    </div>
                  </div>

                  {/* Row 2: System prompt (large) */}
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                      <label className="t-label" style={{ margin: 0 }}>시스템 프롬프트</label>
                      <span style={{ fontSize: 11, color: "var(--text-3)" }}>AI의 역할·성격·답변 스타일을 자유롭게 정의하세요</span>
                    </div>
                    <textarea className="t-input"
                      rows={14}
                      placeholder={`예시:\n당신은 마케팅 전략 전문가 AI입니다.\n\n역할:\n- 데이터 기반의 마케팅 인사이트 제공\n- 캠페인 아이디어 및 카피라이팅 지원\n- 경쟁사 분석 및 시장 트렌드 파악\n\n답변 스타일:\n- 항상 한국어로 답변\n- 실행 가능한 액션 아이템 포함\n- 구체적인 수치와 예시 활용\n- 불필요한 내용 없이 핵심만 간결하게`}
                      style={{ resize: "vertical", fontFamily: "inherit", lineHeight: 1.65, fontSize: 13, minHeight: 260 }}
                      value={createForm.system_prompt}
                      onChange={(e) => setCreateForm({ ...createForm, system_prompt: e.target.value })} />
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
                      <span style={{ fontSize: 11, color: "var(--text-3)" }}>{createForm.system_prompt.length}자</span>
                    </div>
                  </div>

                  {/* Preview card */}
                  {createForm.name && (
                    <div style={{ border: "1px solid rgba(99,102,241,0.3)", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, background: "rgba(99,102,241,0.04)" }}>
                      <div style={{ width: 48, height: 48, borderRadius: 11, background: "rgba(99,102,241,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, flexShrink: 0 }}>{createForm.avatar_emoji || "🤖"}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 15 }}>{createForm.name}
                          <span style={{ fontWeight: 400, fontSize: 12, color: "var(--text-3)", fontFamily: "monospace", marginLeft: 8 }}>{createForm.slug ? `@${createForm.slug}` : ""}</span>
                        </div>
                        <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {createForm.system_prompt ? createForm.system_prompt.split("\n")[0].slice(0, 80) : "시스템 프롬프트를 입력하세요"}
                        </div>
                      </div>
                      <span style={{ fontSize: 10, background: "rgba(99,102,241,0.12)", color: "var(--p-500)", padding: "2px 8px", borderRadius: 6, fontWeight: 700, flexShrink: 0 }}>미리보기</span>
                    </div>
                  )}

                  <button className="t-btn t-btn-primary" style={{ fontSize: 14, padding: "11px 0", borderRadius: 10 }}
                    onClick={createPersona} disabled={creating || !createForm.name.trim() || !createForm.slug.trim()}>
                    {creating ? "생성 중…" : `${createForm.avatar_emoji || "🤖"} ${createForm.name || "페르소나"} 생성하기`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Create Room Modal ─────────────────────────────────────────────────────────
function CreateRoomModal({ onClose, onCreated }: { onClose: () => void; onCreated: (r: Room) => void }) {
  const [form, setForm] = useState({ name: "", type: "GROUP", description: "", icon: "💬" });
  const [memberSearch, setMemberSearch] = useState("");
  const [allUsers, setAllUsers] = useState<UserSummary[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selected, setSelected] = useState<UserSummary[]>([]);
  const isDirect = false;   // 1:1 DM 은 사이드바 멤버 더블클릭으로 — 이 모달은 채널 전용

  // 전체 회원 목록을 처음에 불러와, 검색어로 실시간 필터한다(검색 버튼 없이 바로 보임).
  useEffect(() => {
    api.get<any>("/users?size=300")
      .then((r) => setAllUsers(Array.isArray(r.data) ? r.data : (r.data?.data ?? r.data?.items ?? [])))
      .catch(() => {})
      .finally(() => setLoadingUsers(false));
  }, []);

  const q = memberSearch.trim().toLowerCase();
  const results = allUsers.filter((u) =>
    !q || u.name.toLowerCase().includes(q) || (u.username?.toLowerCase() ?? "").includes(q)
  );

  // DM(1:1)은 한 명만 선택; 그룹/부서/프로젝트는 다중 선택.
  const toggle = (u: UserSummary) => setSelected((p) => {
    if (isDirect) return p.some((s) => s.id === u.id) ? [] : [u];
    return p.some((s) => s.id === u.id) ? p.filter((s) => s.id !== u.id) : [...p, u];
  });

  const create = async () => {
    if (!form.name.trim()) return;
    if (isDirect && selected.length !== 1) { toast.warn("1:1 DM은 상대 1명을 선택하세요"); return; }
    try {
      const r = await api.post<Room>("/chat/rooms", { name: form.name, type: form.type, icon: form.icon || undefined, description: form.description || undefined, member_ids: selected.map((s) => s.id) });
      toast.success("채팅방 생성됨"); onCreated(r.data);
    } catch (e: any) { toast.error("생성 실패", e?.response?.data?.error?.message ?? ""); }
  };

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={(e) => e.stopPropagation()} style={{ width: 500 }}>
        <div style={{ padding: "18px 20px", borderBottom: "1px solid var(--border)" }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>새 채널 만들기</h3>
        </div>
        <div style={{ padding: 20, display: "grid", gap: 14 }}>
          <div>
            <label className="t-label">채널 이름 *</label>
            <input className="t-input" value={form.name} placeholder="예: 마케팅-팀" autoFocus
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && create()} />
          </div>
          <div>
            <label className="t-label">설명 <span style={{ fontWeight: 400, color: "var(--text-3)" }}>(선택)</span></label>
            <input className="t-input" value={form.description} placeholder="이 채널의 목적을 입력하세요"
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <label className="t-label">아이콘</label>
            <RoomIconPicker value={form.icon} onChange={(v) => setForm({ ...form, icon: v })} />
          </div>
          <div>
            <label className="t-label">{isDirect ? "대화 상대 선택 " : "멤버 초대 "}<span style={{ fontWeight: 400, color: "var(--text-3)" }}>{isDirect ? "(1명)" : "(선택)"}</span></label>
            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
              <input className="t-input" style={{ flex: 1 }} value={memberSearch} placeholder="이름으로 검색…"
                onChange={(e) => setMemberSearch(e.target.value)} />
            </div>
            {loadingUsers ? (
              <div style={{ fontSize: 12, color: "var(--text-3)", textAlign: "center", padding: "12px 0" }}>회원 목록 불러오는 중…</div>
            ) : results.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text-3)", textAlign: "center", padding: "12px 0" }}>{q ? "검색 결과 없음" : "회원이 없습니다"}</div>
            ) : (
              <div style={{ border: "1px solid var(--border)", borderRadius: 8, maxHeight: 200, overflowY: "auto" }}>
                {results.map((u) => {
                  const isSel = selected.some((s) => s.id === u.id);
                  return (
                    <div key={u.id} onClick={() => toggle(u)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", fontSize: 13, cursor: "pointer", background: isSel ? "rgba(99,102,241,0.08)" : undefined }}>
                      <div style={{ width: 24, height: 24, borderRadius: "50%", background: "var(--panel)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>{u.name[0]?.toUpperCase()}</div>
                      <span style={{ flex: 1 }}>{u.name}</span>
                      {u.username && <span style={{ fontSize: 11, color: "var(--text-3)" }}>@{u.username}</span>}
                      {isSel && <span style={{ color: "var(--p-500)", fontWeight: 700 }}>✓</span>}
                    </div>
                  );
                })}
              </div>
            )}
            {selected.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }}>
                {selected.map((s) => (
                  <span key={s.id} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(99,102,241,0.1)", color: "var(--p-500)", padding: "3px 8px", borderRadius: 12, fontSize: 12 }}>
                    {s.name}
                    <button style={{ border: "none", background: "none", cursor: "pointer", fontSize: 11, color: "var(--p-400)", padding: 0, lineHeight: 1 }} onClick={() => toggle(s)}>✕</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="t-btn" onClick={onClose}>취소</button>
          <button className="t-btn t-btn-primary" onClick={create} disabled={!form.name.trim()}>채널 만들기</button>
        </div>
      </div>
    </div>
  );
}

// ── Emoji Picker ──────────────────────────────────────────────────────────────
// Uses createPortal + position:fixed so it renders above any overflow:hidden parent.
function EmojiPicker({ value, onChange }: { value: string; onChange: (emoji: string) => void }) {
  const [open, setOpen] = useState(false);
  const [catIdx, setCatIdx] = useState(0);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const palRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        palRef.current && !palRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggle = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: Math.min(r.left, window.innerWidth - 296) });
    }
    setOpen((v) => !v);
  };

  const palette = open ? createPortal(
    <div ref={palRef} style={{
      position: "fixed", top: pos.top, left: pos.left, zIndex: 9999,
      width: 288, background: "var(--bg-0, #ffffff)", border: "1px solid var(--border)",
      borderRadius: 14, boxShadow: "0 8px 32px rgba(0,0,0,0.22)", overflow: "hidden",
    }}>
      {/* Category tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid var(--border)" }}>
        {EMOJI_CATS.map((cat, i) => (
          <button key={i} onClick={() => setCatIdx(i)} title={cat.label}
            style={{
              flex: 1, padding: "8px 0", border: "none", cursor: "pointer", fontSize: 17,
              background: catIdx === i ? "rgba(99,102,241,0.1)" : "transparent",
              borderBottom: catIdx === i ? "2px solid var(--p-500)" : "2px solid transparent",
              transition: "all 0.12s",
            }}>
            {cat.icon}
          </button>
        ))}
      </div>
      <div style={{ padding: "5px 10px 2px", fontSize: 10, color: "var(--text-3)", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
        {EMOJI_CATS[catIdx].label}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 2, padding: "4px 8px 10px" }}>
        {EMOJI_CATS[catIdx].emojis.map((emoji, i) => (
          <button key={i}
            onClick={() => { onChange(emoji); setOpen(false); }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "rgba(99,102,241,0.14)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = value === emoji ? "rgba(99,102,241,0.2)" : "transparent"; }}
            style={{
              border: "none", borderRadius: 7, cursor: "pointer", fontSize: 21,
              padding: "5px 0", textAlign: "center" as const, lineHeight: "1",
              background: value === emoji ? "rgba(99,102,241,0.2)" : "transparent",
              transition: "background 0.1s",
            }}>
            {emoji}
          </button>
        ))}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <div>
      <button ref={btnRef} type="button" onClick={toggle}
        style={{
          width: 56, height: 56, borderRadius: 12, border: "1px solid var(--border)",
          background: "var(--panel)", cursor: "pointer", fontSize: 28,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "border-color 0.15s",
        }}
        title="이모지 선택">
        {value || "🤖"}
      </button>
      {palette}
    </div>
  );
}
